import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ArbitragePage.css";
import {
  classifySportForAnalysisTab,
  formatAnalysisTime,
  formatAnalysisOddsDisplay,
  getAnalysisStatusLabel,
  getAnalysisTranslations,
  translateLeagueDisplay,
  translateMarketDisplay,
  translateMarketDisplayWithPeriodSplit,
  translateSelectionDisplay,
  buildArbitrageMiddleMarketRowLabel,
  translateArbMarketLabel,
  getArbDisplayTranslatorBag,
} from "./analysisTranslate";
import { normalizeSharpMarketKeyForDisplay } from "./analysisDataMaps";
import { isSuspiciousEpochLikeMs } from "./analysisTimeUtils";
import * as sharpArbAdapterNs from "./sharpArbAdapter";

// ─── Shared odds format sync ───────────────────────────────────
const ANALYSIS_ODDS_FORMAT_KEY = "analysis_options_odds_format";
const VALID_ODDS_FORMATS = ["hk", "decimal", "american"];

function readOddsFormatFromStorage() {
  try {
    const v = localStorage.getItem(ANALYSIS_ODDS_FORMAT_KEY);
    if (v && VALID_ODDS_FORMATS.includes(v.toLowerCase())) return v.toLowerCase();
  } catch { /* ignore */ }
  return "hk";
}

function useOddsFormat() {
  const [oddsFormat, setOddsFormat] = React.useState(readOddsFormatFromStorage);
  React.useEffect(() => {
    function sync() {
      const next = readOddsFormatFromStorage();
      setOddsFormat((prev) => (prev !== next ? next : prev));
    }
    // Cross-tab sync (storage event fires in other windows)
    window.addEventListener("storage", sync);
    // Same-window sync via custom event dispatched by settings page
    window.addEventListener("analysis_odds_format_changed", sync);
    // Fallback poll every 300ms — handles same-window SPA tab switching
    const iv = setInterval(sync, 300);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("analysis_odds_format_changed", sync);
      clearInterval(iv);
    };
  }, []);
  return oddsFormat;
}
// ───────────────────────────────────────────────────────────────

const sharpArbAdapter =
  (sharpArbAdapterNs && sharpArbAdapterNs.default) || sharpArbAdapterNs || {};

const ARB_V2_SOURCE_PREVIEW_ENABLED = false;
const ARB_V2_PRODUCTION_MODE_ENABLED = true;
const ARB_V2_SOURCE_POLL_MS = 8000;

function getArbV2RequestMode() {
  if (ARB_V2_SOURCE_PREVIEW_ENABLED) return "source";
  if (ARB_V2_PRODUCTION_MODE_ENABLED) return "production";
  return "verified";
}

const arbDisplayHelpers = {
  pickArbDisplayMarketSource:
    typeof sharpArbAdapter.pickArbDisplayMarketSource === "function"
      ? sharpArbAdapter.pickArbDisplayMarketSource
      : null,
  pickArbDisplayPeriodScope:
    typeof sharpArbAdapter.pickArbDisplayPeriodScope === "function"
      ? sharpArbAdapter.pickArbDisplayPeriodScope
      : null,
  pickArbDisplayLine:
    typeof sharpArbAdapter.pickArbDisplayLine === "function"
      ? sharpArbAdapter.pickArbDisplayLine
      : null,
  buildArbPickOddsHkDisplay:
    typeof sharpArbAdapter.buildArbPickOddsHkDisplay === "function"
      ? sharpArbAdapter.buildArbPickOddsHkDisplay
      : null,
  arbDisplayLooksLikeRawMarketKey:
    typeof sharpArbAdapter.arbDisplayLooksLikeRawMarketKey === "function"
      ? sharpArbAdapter.arbDisplayLooksLikeRawMarketKey
      : null,
  arbHumanizeRawMarketKey:
    typeof sharpArbAdapter.arbHumanizeRawMarketKey === "function"
      ? sharpArbAdapter.arbHumanizeRawMarketKey
      : null,
  // Arb leg display helpers (display-only).
  getArbMarketFamily:
    typeof sharpArbAdapter.getArbMarketFamily === "function"
      ? sharpArbAdapter.getArbMarketFamily
      : null,
  isArbHandicapLikeMarket:
    typeof sharpArbAdapter.isArbHandicapLikeMarket === "function"
      ? sharpArbAdapter.isArbHandicapLikeMarket
      : null,
  isArbTotalLikeMarket:
    typeof sharpArbAdapter.isArbTotalLikeMarket === "function"
      ? sharpArbAdapter.isArbTotalLikeMarket
      : null,
  pickArbLegLine:
    typeof sharpArbAdapter.pickArbLegLine === "function"
      ? sharpArbAdapter.pickArbLegLine
      : null,
  formatArbLegLine:
    typeof sharpArbAdapter.formatArbLegLine === "function"
      ? sharpArbAdapter.formatArbLegLine
      : null,
  buildArbLegDisplayLabel:
    typeof sharpArbAdapter.buildArbLegDisplayLabel === "function"
      ? sharpArbAdapter.buildArbLegDisplayLabel
      : null,
  buildArbMarketDisplayLabel:
    typeof sharpArbAdapter.buildArbMarketDisplayLabel === "function"
      ? sharpArbAdapter.buildArbMarketDisplayLabel
      : null,
  getArbCanonicalMarketType:
    typeof sharpArbAdapter.getArbCanonicalMarketType === "function"
      ? sharpArbAdapter.getArbCanonicalMarketType
      : null,
  getArbCanonicalTopLevelLine:
    typeof sharpArbAdapter.getArbCanonicalTopLevelLine === "function"
      ? sharpArbAdapter.getArbCanonicalTopLevelLine
      : null,
  getArbCanonicalPeriodScope:
    typeof sharpArbAdapter.getArbCanonicalPeriodScope === "function"
      ? sharpArbAdapter.getArbCanonicalPeriodScope
      : null,
  getArbCanonicalMarketFamily:
    typeof sharpArbAdapter.getArbCanonicalMarketFamily === "function"
      ? sharpArbAdapter.getArbCanonicalMarketFamily
      : null,
  getArbCanonicalLegLineInfo:
    typeof sharpArbAdapter.getArbCanonicalLegLineInfo === "function"
      ? sharpArbAdapter.getArbCanonicalLegLineInfo
      : null,
  buildArbCanonicalMarketDisplayLabel:
    typeof sharpArbAdapter.buildArbCanonicalMarketDisplayLabel === "function"
      ? sharpArbAdapter.buildArbCanonicalMarketDisplayLabel
      : null,
  buildArbCanonicalLegDisplayLabel:
    typeof sharpArbAdapter.buildArbCanonicalLegDisplayLabel === "function"
      ? sharpArbAdapter.buildArbCanonicalLegDisplayLabel
      : null,
  buildArbSourceDebugSnapshot:
    typeof sharpArbAdapter.buildArbSourceDebugSnapshot === "function"
      ? sharpArbAdapter.buildArbSourceDebugSnapshot
      : null,
};

let cachedAnalysisApiBase = null;

async function detectAnalysisApiBase() {
  if (cachedAnalysisApiBase !== null) return cachedAnalysisApiBase;
  const candidates = ["", "http://localhost:3001"];
  for (const base of candidates) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 4000);
      const res = await fetch(`${base}/api/_selftest`, { signal: ctrl.signal });
      clearTimeout(t);
      if (!res.ok) continue;
      const j = await res.json().catch(() => null);
      if (j && j.ok === true) {
        cachedAnalysisApiBase = base;
        return base;
      }
    } catch {
      /* next */
    }
  }
  cachedAnalysisApiBase = "";
  return "";
}

function httpBaseToAnalysisWsUrl(base) {
  try {
    const b = String(base ?? "").trim();
    if (!b) {
      if (typeof window === "undefined") return "ws://localhost:3001/api/analysis/ws";
      const p = window.location.protocol === "https:" ? "wss:" : "ws:";
      return `${p}//${window.location.host}/api/analysis/ws`;
    }
    const u = new URL(b, typeof window !== "undefined" ? window.location.href : "http://localhost");
    const wsP = u.protocol === "https:" ? "wss:" : "ws:";
    return `${wsP}//${u.host}/api/analysis/ws`;
  } catch {
    return null;
  }
}

function mapAnalysisServerStatusToArbMeta(st) {
  if (!st || typeof st !== "object") return null;
  const ca = st.cacheUpdatedAt && st.cacheUpdatedAt.arbitrage;
  const liveU = Number(ca?.live || 0);
  const earlyU = Number(ca?.early || 0);
  const pub = Math.max(liveU, earlyU);
  if (!Number.isFinite(pub) || pub <= 0) return null;
  return {
    lastArbitragePublishedAt: pub,
    lastPollFinishedAt: typeof st.now === "number" ? st.now : Date.now(),
    arbitrageRevision: st.analysisRevision,
  };
}

async function copyTextToClipboardSafe(text) {
  const value = String(text || "");
  if (!value) return false;
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    if (typeof document !== "undefined") {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const okExec = document.execCommand && document.execCommand("copy");
      document.body.removeChild(ta);
      return !!okExec;
    }
  } catch {
    /* ignore */
  }
  return false;
}

function buildArbCopyText(item, lang = "en-GB", oddsFormat = "hk") {
  if (!item || typeof item !== "object") return "";
  const lines = [];
  const matchLine = String(item?.match || "").trim();
  if (matchLine) lines.push(matchLine);

  const marketLine =
    getItemSport(item) === "esports"
      ? getEsportsMarketLabel(item, lang)
      : getClassicMarketRowLabel(item, lang);
  if (marketLine) lines.push(marketLine);

  const picks = toArray(item?.picks);
  picks.forEach((pick) => {
    const selectionLabel =
      formatClassicPickPreviewLabel(pick, lang, item, null) ||
      String(pick?.selection ?? pick?.sel ?? "").trim() ||
      "—";
    const bookLabel = String(pick?.book || pick?.sportsbook || "").trim();
    let oddsHk = "";
    if (typeof arbDisplayHelpers.buildArbPickOddsHkDisplay === "function") {
      oddsHk = arbDisplayHelpers.buildArbPickOddsHkDisplay(pick) || "";
    }
    if (!oddsHk) oddsHk = formatAnalysisOddsDisplay(pick?.odds, oddsFormat) || "";
    const suffix = [bookLabel || null, oddsHk ? `@${oddsHk}` : null].filter(Boolean).join(" ");
    lines.push(`- ${selectionLabel}${suffix ? ` (${suffix})` : ""}`);
  });

  const profitPct = toNumber(item?.profitPct, NaN);
  if (Number.isFinite(profitPct)) lines.push(`Profit: +${profitPct.toFixed(2)}%`);

  return lines.filter(Boolean).join("\n").trim();
}

function formatBookDisplayName(raw) {
  const key = String(raw || "").toLowerCase().trim();
  const MAP = {
    "onexbet":   "1xBet",
    "1xbet":     "1xBet",
    "betmgm":    "BetMGM",
    "draftkings":"DraftKings",
    "fanduel":   "FanDuel",
    "pinnacle":  "Pinnacle",
    "betway":    "Betway",
    "bwin":      "bwin",
  };
  return MAP[key] || raw;
}

const PROFIT_OPTIONS = [0.5, 1, 3, 5, 8, 10, 15];

function clampToNearestNumberOption(value, options) {
  const opts = Array.isArray(options) ? options : [];
  if (!opts.length) return value;
  const v = Number(value);
  if (!Number.isFinite(v)) return opts[0];

  let best = opts[0];
  let bestDiff = Math.abs(opts[0] - v);

  for (let i = 1; i < opts.length; i += 1) {
    const opt = opts[i];
    const diff = Math.abs(opt - v);
    if (diff < bestDiff) {
      best = opt;
      bestDiff = diff;
    } else if (diff === bestDiff && opt > best) {
      // Tie-breaker: pick the higher value to be stricter.
      best = opt;
      bestDiff = diff;
    }
  }

  return best;
}

/* --------------------
UTILS
-------------------- */
function uniq(arr) {
  return Array.from(new Set((arr || []).map((x) => String(x)))).filter(Boolean);
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  return [];
}

function areStringArraysEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (String(a[i]) !== String(b[i])) return false;
  }
  return true;
}

function numSigForSignature(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "x";
  return n.toFixed(6);
}

function buildArbitragePickSignature(pick) {
  const book = String(resolvePickBook(pick) || "").trim();
  const sel = String(resolvePickSelection(pick) || "").trim();
  const odds = numSigForSignature(resolvePickOdds(pick));
  return `${book}|${sel}|${odds}`;
}

function buildArbitrageItemSignature(item) {
  const id = getItemStableIdentityKey(item);
  const profitPct = numSigForSignature(item?.profitPct);
  const confidenceScore = numSigForSignature(item?.confidenceScore);
  const scope = String(normalizeScope(item?.scope) || "");
  const sport = String(item?.sport || getItemSport(item) || "");
  const league = String(item?.league || "").trim();
  const match = String(item?.match || buildMatchLabel(item) || "").trim();
  const market = String(item?.market || "").trim();
  const line = item?.line == null ? "" : String(item.line);
  const map = String(item?.map || item?.mapName || "").trim();
  const game = String(item?.game || "").trim();
  const kickoff = String(
    item?.kickoff ?? item?.kickoffDate ?? item?.kickoffTs ?? ""
  ).trim();

  const picks = toArray(item?.picks);
  const pickSigs = picks
    .map((p) => buildArbitragePickSignature(p))
    .sort((a, b) => a.localeCompare(b));
  const picksPart = pickSigs.join("~");

  return [id, profitPct, confidenceScore, scope, sport, league, match, market, line, map, game, kickoff, picksPart].join(
    "\x1f"
  );
}

function getItemStableIdentityKey(item) {
  const stableKey =
    item && (item.stableKey || item.semanticOpportunityFingerprint || item.opportunityFingerprint);
  const fallbackId = item && item.id;
  const key = String(stableKey || fallbackId || "");
  return key;
}

function areArbitrageCollectionsEquivalent(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (buildArbitrageItemSignature(a[i]) !== buildArbitrageItemSignature(b[i])) {
      return false;
    }
  }
  return true;
}

function toNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function fmtTime(ts, lang = "en-GB", timeZone) {
  return formatAnalysisTime(ts, lang, timeZone);
}

function pickVisibleScanTs(statusPayload) {
  if (!statusPayload || typeof statusPayload !== "object") return null;
  const pub = statusPayload.lastArbitragePublishedAt;
  const poll = statusPayload.lastPollFinishedAt;
  if (typeof pub === "number" && pub > 0) return pub;
  if (typeof poll === "number" && poll > 0) return poll;
  return null;
}

function getStatusLabel(item, t, lang = "en-GB", timeZone) {
  return getAnalysisStatusLabel(item, t, lang, "—", timeZone);
}

function normalizeIncomingArbV2Row(row, options = {}) {
  if (!row || typeof row !== "object") return row;
  const sourcePreviewMode = options?.sourcePreviewMode === true;
  const out = { ...row };
  if (sourcePreviewMode) out.arbSourcePreview = true;
  return out;
}

// Single source of truth for a row's *real* sport meaning (presentation +
// semantics). Delegates entirely to the shared classifySportForAnalysisTab()
// classifier from analysisDataMaps.js (via analysisTranslate) — no local
// regex classification is performed here. Returns one of:
//   "football" | "tennis" | "basketball" | "esports" | "unsupported"
function getArbCanonicalSport(item) {
  if (!item || typeof item !== "object") return "unsupported";
  const rawSport =
    item?.sport ??
    item?.sportKey ??
    item?.sportName ??
    item?.category ??
    item?.gameType ??
    "";
  const rawLeague =
    item?.league ??
    item?.leagueName ??
    item?.competition ??
    item?.competitionName ??
    item?.tournament ??
    item?.tournamentName ??
    "";
  const match =
    item?.match ??
    item?.matchName ??
    item?.eventName ??
    item?.event_name ??
    "";
  return classifySportForAnalysisTab(rawSport, { league: rawLeague, match });
}

// Presentation/semantics accessor — always the real canonical sport. Never
// use this for tab filtering/routing (see getArbSportFilterKey below).
function getItemSport(item) {
  return getArbCanonicalSport(item);
}

function normalizeArbSportToken(rawValue) {
  return String(rawValue ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

// Visible Arb tab routing key — maps the canonical sport to the internal
// tab/badge key. Sports without a dedicated tab (baseball, hockey, cricket,
// rugby, NFL/American football, table tennis, etc.) are canonical
// "unsupported" and are routed into the Esports tab as a fallback bucket,
// WITHOUT being treated as real esports anywhere else.
function getArbSportFilterKey(item) {
  if (!item || typeof item !== "object") return "";
  const canonical = getArbCanonicalSport(item);
  if (canonical === "football") return "soccer";
  if (canonical === "tennis") return "tennis";
  if (canonical === "basketball") return "basketball";
  if (canonical === "esports") return "esports";
  // "unsupported" (NFL, table tennis, baseball, hockey, cricket, rugby,
  // volleyball, unknown, ...) falls back into the Esports tab route.
  return "esports";
}

function mapSportTabToCanonicalSportKey(sportTab) {
  const token = normalizeArbSportToken(sportTab);
  if (!token || token === "all") return "all";
  if (token === "football" || token === "soccer") return "soccer";
  if (token === "basketball") return "basketball";
  if (token === "tennis") return "tennis";
  if (token === "esports" || token === "esport") return "esports";
  if (token === "baseball") return "baseball";
  if (token === "hockey") return "hockey";
  return token;
}

function itemMatchesArbSportTab(item, sportTab) {
  const activeSportKey = mapSportTabToCanonicalSportKey(sportTab);
  if (activeSportKey === "all") return true;
  const itemSportKey = getArbSportFilterKey(item);
  if (!itemSportKey) return false;
  return itemSportKey === activeSportKey;
}

function humanizeArbLeagueLabel(rawLeague) {
  const raw = String(rawLeague || "").trim();
  if (!raw) return "";
  if (/^[a-z]{2,5}$/i.test(raw)) return raw.toUpperCase();
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function formatArbSportLabelFromKey(sportKey, lang = "en-GB") {
  const isVi = String(lang || "").toLowerCase().startsWith("vi");
  if (sportKey === "soccer") return isVi ? "Bóng đá" : "Football";
  if (sportKey === "basketball") return isVi ? "Bóng rổ" : "Basketball";
  if (sportKey === "tennis") return isVi ? "Tennis" : "Tennis";
  if (sportKey === "esports") return isVi ? "Esports" : "Esports";
  if (sportKey === "baseball") return isVi ? "Bóng chày" : "Baseball";
  if (sportKey === "hockey") return isVi ? "Khúc côn cầu" : "Hockey";
  return "";
}

function formatArbSportLeagueLabel(item, lang = "en-GB") {
  const ev = item?.event || item?.fixture?.event;
  const rawLeagueTop = String(item?.league || "").trim();
  const rawLeagueNested = !rawLeagueTop
    ? leagueStringFromUnknown(item?.fixture?.league) ||
      leagueStringFromUnknown(item?.fixture?.leagueName) ||
      leagueStringFromUnknown(item?.fixture?.competition) ||
      leagueStringFromUnknown(item?.fixture?.competitionName) ||
      leagueStringFromUnknown(item?.fixture?.tournamentName) ||
      leagueStringFromUnknown(ev?.league) ||
      leagueStringFromUnknown(ev?.leagueName) ||
      leagueStringFromUnknown(ev?.competition) ||
      leagueStringFromUnknown(ev?.competitionName) ||
      ""
    : "";
  const rawLeague = rawLeagueTop || rawLeagueNested;
  const translatedLeague = translateLeagueDisplay(rawLeague, lang);
  const leagueLabel = translatedLeague || humanizeArbLeagueLabel(rawLeague);
  // Use the canonical sport (not the route helper) to decide whether this is
  // REAL esports. Sports that only fall back into the Esports tab (NFL,
  // table tennis, baseball, hockey, cricket, ...) must never show the
  // "Esports" league fallback label — they keep the generic "—" fallback.
  const canonicalSport = getArbCanonicalSport(item);
  if (canonicalSport === "esports") {
    if (leagueLabel) return leagueLabel;
    return formatArbSportLabelFromKey("esports", lang) || "—";
  }
  if (leagueLabel) return leagueLabel;
  return "—";
}

function leagueStringFromUnknown(v) {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v === "object") {
    return String(
      v.name || v["@name"] || v.league_name || v.leagueName || v.title || v.label || v.competition || ""
    ).trim();
  }
  return "";
}

function isMeaningfulLineForCard(line) {
  const s = line == null ? "" : String(line).trim();
  if (!s) return false;
  if (s === "—" || s === "-") return false;
  return true;
}

function normalizeArbMarketForTranslate(rawMarket) {
  const raw = String(rawMarket ?? "").trim();
  if (!raw) return "";
  const key = normalizeSharpMarketKeyForDisplay(rawMarket);
  switch (key) {
    case "total goals":
    case "goals total":
      return "total goals";
    case "total points":
    case "points total":
    case "total rounds":
    case "rounds total":
    case "over/under":
      return "Totals";
    case "spread":
      return "spread";
    case "point spread":
      return "point spread";
    case "run line":
      return "run line";
    case "team total":
    case "team totals":
      return "team total";
    case "team total goals":
      return "team total goals";
    case "total runs":
      return "total runs";
    case "total games":
      return "total games";
    case "set handicap":
      return "set handicap";
    case "game handicap":
      return "game handicap";
    case "asian handicap":
      return "Asian Handicap";
    case "moneyline":
      return "Moneyline";
    case "1x2":
      return "1X2";
    case "full time result":
    case "fulltime result":
    case "full-time result":
      return "1X2";
    case "corner match bet":
    case "asian handicap corners":
    case "corners asian handicap":
      return key;
    case "set moneyline":
      return "set moneyline";
    case "puck line":
      return "puck line";
    case "player made threes":
      return "player made threes";
    case "1st half":
      return "1st half";
    case "2nd half":
      return "2nd half";
    case "1st quarter":
      return "1st quarter";
    case "2nd quarter":
      return "2nd quarter";
    case "player prop":
      return "player prop";
    default:
      return key || raw;
  }
}

function normalizeLegSelectionForDisplay(sel) {
  const s = String(sel ?? "").trim();
  if (!s) return { baseForTranslate: "", extractedLine: "" };
  const m = /^(Over|Under)\s+([-+]?\d+(?:\.\d+)?)\s*$/i.exec(s);
  if (m) {
    const canonical = /^over$/i.test(m[1]) ? "Over" : "Under";
    return { baseForTranslate: canonical, extractedLine: m[2] };
  }
  return { baseForTranslate: s, extractedLine: "" };
}

function selectionAlreadyContainsLine(sel, line) {
  const rawSel = String(sel ?? "").trim();
  const rawLine = String(line ?? "").trim();
  if (!rawSel || !rawLine) return false;
  if (!isMeaningfulLineForCard(rawLine)) return false;
  const lineCore = rawLine.replace(/\s+/g, "");
  if (!lineCore) return false;
  const escaped = lineCore.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(^|[^0-9.,])${escaped}($|[^0-9])`, "i");
  return re.test(rawSel);
}

function isSingleTotalsLineToken(value) {
  const s = String(value ?? "").trim();
  if (!isMeaningfulLineForCard(s)) return false;
  // Avoid corridor / composite lines like "12.5 / 17.5" or "12.5/17.5".
  if (/[\\/|]/.test(s)) return false;
  // Avoid combined lines "A - B" (sometimes corridors) and other non-numeric tails.
  if (/\s-\s/.test(s)) return false;
  return /^[-+]?\d+(?:\.\d+)?$/.test(s);
}

/** Prefer verified/backend line first so handicap sign cannot be confused with comparable-only item.line. */
function effectivePickLineForDisplay(pick) {
  const cands = [
    pick?.verifiedLine,
    pick?.line,
    pick?.handicap,
    pick?.point,
    pick?.total,
    pick?.overLine,
    pick?.underLine,
  ];
  for (let i = 0; i < cands.length; i += 1) {
    const v = cands[i];
    if (isMeaningfulLineForCard(v)) return String(v).trim();
  }
  return "";
}

/** Mirror server.js sharpMarketKey for narrow 3-way 1X2 detection. */
function sharpMarketKeyForThreeWayUi(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");
}

function arbMarketTypeFromArbItemUi(item) {
  return item?.market_type || item?.marketType || item?.market || "";
}

/**
 * Narrow 3-way markets only (align with server.js arbMarketIsThreeWay1x2).
 */
function arbMarketIsThreeWay1x2Ui(mtRaw) {
  const k0 = sharpMarketKeyForThreeWayUi(mtRaw);
  const k = k0.replace(/-/g, "");
  if (!k) return false;
  if (k === "moneyline") return false;
  if (k.includes("drawnobet") || k === "dnb") return false;
  if (k.includes("2way") || k.includes("twoway")) return false;
  if (k === "matchwinner") return false;
  if (k === "1x2") return true;
  if (k.includes("fulltimeresult")) return true;
  if (k === "threewaymoneyline") return true;
  if (k.includes("3way") && k.includes("moneyline")) return true;
  if (k.includes("matchwinner") && k.includes("3way")) return true;
  if (k.includes("fulltime") && k.includes("winner") && k.includes("3way")) return true;
  if (k.includes("winner") && k.includes("3way")) return true;
  return false;
}

/** Align with server.js arbLooksLikeSoccerThreeWayWinner — display only; backend is source of truth. */
function arbLooksLikeSoccerThreeWayWinnerUi(item) {
  if (!item || typeof item !== "object") return false;
  if (item.is_player_prop === true) return false;
  const sport = String(item?.sport ?? "")
    .trim()
    .toLowerCase();
  if (!sport.includes("football") && !sport.includes("soccer")) return false;
  const picks = Array.isArray(item?.picks) ? item.picks : [];
  if (picks.length !== 3) return false;
  const mt = arbMarketTypeFromArbItemUi(item);
  if (arbMarketIsThreeWay1x2Ui(mt)) return false;
  const k = sharpMarketKeyForThreeWayUi(mt);
  if (
    k.includes("corner") ||
    k.includes("card") ||
    k.includes("booking") ||
    k.includes("offside") ||
    k.includes("total") ||
    k.includes("handicap") ||
    k.includes("spread") ||
    k.includes("overunder") ||
    k === "ou"
  ) {
    return false;
  }
  const teams = eventTeamsFromArbItemUi(item);
  if (!teams.home || !teams.away) return false;
  if (normalizeTeamCompareTokenUi(teams.home) === normalizeTeamCompareTokenUi(teams.away)) return false;
  let drawCt = 0;
  let homeCt = 0;
  let awayCt = 0;
  for (let i = 0; i < 3; i += 1) {
    const o = classifyThreeWayOutcomeSecondPassUi(picks[i], teams.home, teams.away);
    if (o === "DRAW") drawCt += 1;
    else if (o === "HOME") homeCt += 1;
    else if (o === "AWAY") awayCt += 1;
    else return false;
  }
  return drawCt === 1 && homeCt === 1 && awayCt === 1;
}

function stripOptionalClubSuffixTokenUi(t) {
  return String(t ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+(fc|cf|afc|sc)\s*$/i, "")
    .replace(/^(fc|cf|afc|sc)\s+/i, "")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeTeamCompareTokenUi(s) {
  let t = String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[.,'"]/g, "")
    .replace(/[–—-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  t = stripOptionalClubSuffixTokenUi(t);
  return t.replace(/\s+/g, " ").trim();
}

function parseEventNameTeamsFromStringForUi(eventName) {
  const s = String(eventName ?? "").trim();
  if (!s) return { home: "", away: "" };
  const mVs =
    /^(.+?)\s+vs\.?\s+(.+)$/i.exec(s) ||
    /^(.+?)\s+v\s+(.+)$/i.exec(s) ||
    /^(.+?)\s+[–-]\s+(.+)$/.exec(s);
  if (mVs) return { home: mVs[1].trim(), away: mVs[2].trim() };
  const mAt = /^(.+?)\s+@\s+(.+)$/i.exec(s);
  if (mAt) {
    return { home: mAt[2].trim(), away: mAt[1].trim() };
  }
  return { home: "", away: "" };
}

function eventTeamsFromArbItemUi(item) {
  if (!item || typeof item !== "object") return { home: "", away: "" };
  let h =
    item.home_team ??
    item.homeTeamName ??
    item.homeTeam ??
    item?.teams?.home?.name ??
    item?.event?.homeTeam ??
    "";
  let a =
    item.away_team ??
    item.awayTeamName ??
    item.awayTeam ??
    item?.teams?.away?.name ??
    item?.event?.awayTeam ??
    "";
  h = h != null ? String(h).trim() : "";
  a = a != null ? String(a).trim() : "";
  if (h && a) return { home: h, away: a };
  const parsed = parseEventNameTeamsFromStringForUi(
    String(item.event_name || item.eventName || item.match || "").trim()
  );
  return { home: parsed.home || "", away: parsed.away || "" };
}

function isDrawLikeSelectionTextUi(sel) {
  const t = normalizeTeamCompareTokenUi(sel);
  if (!t) return false;
  if (t === "draw" || t === "tie" || t === "x" || t === "the draw") return true;
  if (/^(draw|tie|stalemate)$/i.test(String(sel).trim())) return true;
  return false;
}

function selectionTextMatchesSingleTeamUi(sel, team) {
  const x = normalizeTeamCompareTokenUi(sel);
  const y = normalizeTeamCompareTokenUi(team);
  if (!x || !y) return false;
  if (x === y) return true;
  const x0 = stripOptionalClubSuffixTokenUi(x);
  const y0 = stripOptionalClubSuffixTokenUi(y);
  if (x0 && y0 && x0 === y0 && x0.length >= 4) return true;
  if (x0.length >= 6 && y0.length >= 6 && (y0.includes(x0) || x0.includes(y0))) return true;
  if (x.length >= 6 && y.length >= 6 && (y.includes(x) || x.includes(y))) return true;
  const tw = y.split(/\s+/).filter((w) => w.length >= 3);
  if (tw.length >= 2) {
    let hits = 0;
    for (let i = 0; i < tw.length; i += 1) {
      if (x.includes(tw[i])) hits += 1;
    }
    if (hits >= 2) return true;
  }
  return false;
}

function sharpSelNormUi(s) {
  return String(s ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * Mirror server.js classifyThreeWayOutcome. Prefer verified structured fields (selection_type, side, selection) before raw sel text.
 * @returns {"HOME"|"DRAW"|"AWAY"|null}
 */
function classifyThreeWayOutcomeUi(leg, homeTeam, awayTeam) {
  const stCandidates = [leg?.selection_type, leg?.side, leg?.selection];
  for (let i = 0; i < stCandidates.length; i += 1) {
    const v = stCandidates[i];
    if (v == null || v === "") continue;
    const st = String(v).trim().toLowerCase();
    if (st === "home" || st === "h" || st === "1") return "HOME";
    if (st === "away" || st === "a" || st === "2") return "AWAY";
    if (st === "draw" || st === "d" || st === "x") return "DRAW";
  }

  // Only after selection_type / side / selection: numeric codes and team-name heuristics on raw text.
  const selRaw = String(leg?.selection ?? leg?.sel ?? "").trim();
  if (selRaw === "1") return "HOME";
  if (selRaw === "2") return "AWAY";
  if (selRaw === "X" || selRaw === "x") return "DRAW";

  const sel = sharpSelNormUi(leg?.selection ?? leg?.sel ?? "");
  if (!sel) return null;
  if (isDrawLikeSelectionTextUi(sel)) return "DRAW";

  const hOk = selectionTextMatchesSingleTeamUi(sel, homeTeam);
  const aOk = selectionTextMatchesSingleTeamUi(sel, awayTeam);
  if (hOk && aOk) return null;
  if (hOk) return "HOME";
  if (aOk) return "AWAY";
  return null;
}

/** Align with server.js classifyThreeWayOutcomeSecondPass for display only; backend is authoritative. */
function classifyThreeWayOutcomeSecondPassUi(leg, homeTeam, awayTeam) {
  const o0 = classifyThreeWayOutcomeUi(leg, homeTeam, awayTeam);
  if (o0) return o0;
  const rs = String(leg?.selection ?? leg?.sel ?? "").trim();
  const stripped = rs.replace(/\s*[-+]\s*\d+(?:\.\d+)?\s*$/, "").trim();
  if (!stripped || stripped === rs) return null;
  return classifyThreeWayOutcomeUi({ ...leg, selection: stripped, sel: stripped }, homeTeam, awayTeam);
}

function formatClassicPickPreviewLabel(pick, lang = "en-GB", parentItem = null, matchedRawLeg = null) {
  const mtUi = arbMarketTypeFromArbItemUi(parentItem);
  if (
    parentItem &&
    (arbMarketIsThreeWay1x2Ui(mtUi) || arbLooksLikeSoccerThreeWayWinnerUi(parentItem))
  ) {
    const picks = Array.isArray(parentItem.picks) ? parentItem.picks : [];
    if (picks.length === 3) {
      const teams = eventTeamsFromArbItemUi(parentItem);
      if (teams.home && teams.away) {
        const outcome =
          classifyThreeWayOutcomeUi(pick, teams.home, teams.away) ||
          classifyThreeWayOutcomeSecondPassUi(pick, teams.home, teams.away);
        if (outcome === "HOME") return teams.home;
        if (outcome === "AWAY") return teams.away;
        if (outcome === "DRAW") {
          return translateSelectionDisplay("Draw", lang) || "Draw";
        }
      }
    }
  }

  // Prefer canonical Sharp-source display resolver for all Arb families.
  if (parentItem && typeof arbDisplayHelpers.buildArbCanonicalLegDisplayLabel === "function") {
    const translatorBag = getArbDisplayTranslatorBag(lang);
    const canon = arbDisplayHelpers.buildArbCanonicalLegDisplayLabel(parentItem, pick, translatorBag);
    if (canon && canon.label && canon.label !== "—") return canon.label;
  }

  // Backward-compatible fallback.
  const arbFamily =
    typeof arbDisplayHelpers.getArbMarketFamily === "function"
      ? arbDisplayHelpers.getArbMarketFamily(parentItem)
      : null;
  if (parentItem && typeof arbDisplayHelpers.buildArbLegDisplayLabel === "function") {
    const translatorBag = getArbDisplayTranslatorBag(lang);
    const legLabel = arbDisplayHelpers.buildArbLegDisplayLabel(parentItem, pick, matchedRawLeg, translatorBag);
    if (legLabel && legLabel !== "—") return legLabel;
  }

  const sel = String(pick?.selection ?? pick?.sel ?? "").trim();
  if (!sel) return "—";
  const { baseForTranslate, extractedLine } = normalizeLegSelectionForDisplay(sel);
  const selectionLabel =
    translateSelectionDisplay(baseForTranslate, lang) || baseForTranslate || "—";
  let linePart = "";
  const rawLine = effectivePickLineForDisplay(pick);
  if (isMeaningfulLineForCard(rawLine)) {
    linePart = String(rawLine).trim();
  } else if (extractedLine && isMeaningfulLineForCard(extractedLine)) {
    linePart = extractedLine;
  } else {
    // NEW fallback: for Over/Under picks (especially tennis totals) where legs omit line,
    // reuse the parent item line when it's a single numeric total (not a corridor).
    const isOu =
      String(baseForTranslate || "").toLowerCase() === "over" ||
      String(baseForTranslate || "").toLowerCase() === "under";
    const parentLine =
      parentItem?.verifiedComparableLine ??
      parentItem?.comparableLine ??
      parentItem?.line ??
      parentItem?.point ??
      parentItem?.handicap ??
      parentItem?.total;
    if (isOu && isSingleTotalsLineToken(parentLine)) {
      linePart = String(parentLine).trim();
    }
  }
  if (linePart && selectionAlreadyContainsLine(selectionLabel, linePart)) {
    linePart = "";
  }

  // If the leg-level resolution didn't yield a line, fall back through the
  // shared adapter helper which prefers verifiedLine/handicap/total/etc.
  if (!linePart && typeof arbDisplayHelpers.pickArbDisplayLine === "function") {
    const adapterLine = arbDisplayHelpers.pickArbDisplayLine(parentItem, pick);
    if (
      adapterLine &&
      isMeaningfulLineForCard(adapterLine) &&
      !selectionAlreadyContainsLine(selectionLabel, adapterLine)
    ) {
      linePart = adapterLine;
    }
  }

  // Guard for total-like markets that fell through the adapter branch: any
  // sign on the line MUST be stripped (team_total, total_goals, etc. always
  // display unsigned). Handicap markets keep the explicit sign.
  if (arbFamily === "total" && linePart) {
    const m = /^[-+]?(\d+(?:\.\d+)?)$/.exec(String(linePart).trim());
    if (m) linePart = m[1];
  }

  // Guard for handicap/spread markets: positive line must show explicit + sign.
  if (
    (arbFamily === "handicap" ||
      (typeof arbDisplayHelpers.isArbHandicapLikeMarket === "function" && arbDisplayHelpers.isArbHandicapLikeMarket(parentItem))) &&
    arbFamily !== "total" &&
    linePart
  ) {
    const m = /^([-+]?)(\d+(?:\.\d+)?)$/.exec(String(linePart).trim());
    if (m) {
      const sign = m[1];
      const mag = m[2];
      const n = Number(`${sign}${mag}`);
      if (Number.isFinite(n)) {
        if (n > 0) linePart = `+${n}`;
        else if (n < 0) linePart = String(n);
        else linePart = "0";
      }
    }
  }

  // Guard: never let a raw underscored market key end up as a "selection".
  if (looksLikeRawArbMarketKey(selectionLabel)) {
    const human =
      (typeof arbDisplayHelpers.arbHumanizeRawMarketKey === "function"
        ? arbDisplayHelpers.arbHumanizeRawMarketKey(selectionLabel)
        : "") || baseForTranslate;
    if (!looksLikeRawArbMarketKey(human)) {
      return linePart ? `${human} · ${linePart}` : human;
    }
  }

  return linePart ? `${selectionLabel} · ${linePart}` : selectionLabel;
}

/**
 * Local guard: rejects strings that still contain raw provider keys
 * (underscored or well-known Sharp identifiers) so the card never
 * shows things like "total_corners" or "asian_handicap_corners".
 */
function looksLikeRawArbMarketKey(label) {
  const s = String(label || "");
  if (!s.trim()) return false;
  if (typeof arbDisplayHelpers.arbDisplayLooksLikeRawMarketKey === "function") {
    if (arbDisplayHelpers.arbDisplayLooksLikeRawMarketKey(s)) return true;
  }
  if (/_/.test(s)) return true;
  return /\b(total_corners|asian_handicap_corners|corner_match_bet|point_spread|total_goals|total_points|total_games|set_handicap|puck_line|run_line|player_made_threes|market_type)\b/i.test(s);
}

function buildArbDisplayPeriodScopeToken(item, sportTab) {
  if (typeof arbDisplayHelpers.pickArbDisplayPeriodScope === "function") {
    return arbDisplayHelpers.pickArbDisplayPeriodScope(item, sportTab) || "";
  }
  return "";
}

function getClassicMarketRowLabel(item, lang = "en-GB") {
  const sportTab = getItemSport(item);
  if (typeof arbDisplayHelpers.buildArbCanonicalMarketDisplayLabel === "function") {
    const itemForHelper = { ...item, _sportTab: sportTab };
    const translatorBag = getArbDisplayTranslatorBag(lang);
    const canon = arbDisplayHelpers.buildArbCanonicalMarketDisplayLabel(itemForHelper, translatorBag);
    if (canon && canon.label && !looksLikeRawArbMarketKey(canon.label)) return canon.label;
  }

  // For handicap/spread AND total-like markets (incl. team_total /
  // 1st_half_team_total / total_corners / corner_handicap) we MUST NOT
  // append a signed item.line into the market header. Each leg displays
  // its own line in the leg row:
  //   - handicap legs render the signed leg line
  //   - total / team_total legs render an unsigned leg line
  // Use the dedicated adapter helper which never appends a header line
  // and applies the Arb-specific Vietnamese overrides for both families.
  const familyForHeader =
    typeof arbDisplayHelpers.getArbMarketFamily === "function"
      ? arbDisplayHelpers.getArbMarketFamily(item)
      : null;
  const useAdapterHeader =
    (familyForHeader === "handicap" || familyForHeader === "total") &&
    typeof arbDisplayHelpers.buildArbMarketDisplayLabel === "function";
  if (useAdapterHeader) {
    const itemForHelper = { ...item, _sportTab: sportTab };
    const translatorBag = {
      lang,
      translateMarket: (raw, lng) => translateArbMarketLabel(raw, lng || lang),
      translateSelection: (raw, lng) => translateSelectionDisplay(raw, lng || lang),
    };
    const label = arbDisplayHelpers.buildArbMarketDisplayLabel(itemForHelper, translatorBag) || "";
    if (label && !looksLikeRawArbMarketKey(label)) return label;
  }

  let label = buildArbitrageMiddleMarketRowLabel(item, lang) || "";

  // Apply Arb-specific Vietnamese label overrides when applicable. We only
  // override when the resolved label still matches the global market name
  // for that key — never for already-localised labels with a scope token,
  // which we leave alone.
  if (label && lang === "vi-VN") {
    const rawSrc =
      (typeof arbDisplayHelpers.pickArbDisplayMarketSource === "function"
        ? arbDisplayHelpers.pickArbDisplayMarketSource(item)
        : item?.market_type || item?.marketType || item?.market || "") || "";
    const overridden = translateArbMarketLabel(rawSrc, lang);
    const globalGeneric = translateMarketDisplay(rawSrc, lang) || "";
    if (overridden && overridden !== globalGeneric) {
      // Replace just the leading market segment (everything before " · ").
      const parts = label.split(" · ");
      if (parts.length > 0) {
        parts[0] = overridden;
        label = parts.join(" · ");
      }
    }
  }

  // Guard: never let a raw provider key (e.g. "total_corners") leak through.
  if (looksLikeRawArbMarketKey(label)) {
    const rawSrc =
      (typeof arbDisplayHelpers.pickArbDisplayMarketSource === "function"
        ? arbDisplayHelpers.pickArbDisplayMarketSource(item)
        : item?.market_type || item?.marketType || item?.market || "") || "";
    const canonical = normalizeArbMarketForTranslate(rawSrc);
    const translated =
      translateArbMarketLabel(rawSrc, lang) ||
      translateMarketDisplay(canonical, lang) ||
      (typeof arbDisplayHelpers.arbHumanizeRawMarketKey === "function"
        ? arbDisplayHelpers.arbHumanizeRawMarketKey(rawSrc)
        : "") ||
      canonical ||
      "";
    if (translated && !looksLikeRawArbMarketKey(translated)) {
      const scope = buildArbDisplayPeriodScopeToken(item, sportTab);
      label = scope ? `${translated} · ${scope}` : translated;
    }
  } else {
    // Append a missing period token when a known display scope can be inferred
    // from the raw market type (e.g. esports map number, basketball Q3/Q4) and
    // the existing label does not already carry it.
    const scope = buildArbDisplayPeriodScopeToken(item, sportTab);
    if (scope) {
      const escaped = scope.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp(`(^|[^a-z0-9])${escaped}($|[^a-z0-9])`, "i");
      if (label && !re.test(label)) {
        label = `${label} · ${scope}`;
      }
    }
  }

  return label;
}

function getClassicSelectionRowLabel(item, lang = "en-GB") {
  const picks = Array.isArray(item?.picks) ? item.picks : [];
  if (!picks.length) return "";
  return uniq(picks.map((p) => formatClassicPickPreviewLabel(p, lang, item))).join(" | ");
}

function getEsportsMarketLabel(item, lang = "en-GB") {
  const _mktSrc = item?.market_type || item?.marketType || item?.market || "";
  const rawMarket = String(_mktSrc).trim();
  const canonicalMarket = normalizeArbMarketForTranslate(_mktSrc);
  let market =
    translateMarketDisplayWithPeriodSplit(rawMarket, lang) ||
    translateMarketDisplay(canonicalMarket, lang) ||
    canonicalMarket ||
    rawMarket;

  // Raw-key guard: never let the card show "map_winner", "total_kills", etc.
  if (looksLikeRawArbMarketKey(market)) {
    const human =
      (typeof arbDisplayHelpers.arbHumanizeRawMarketKey === "function"
        ? arbDisplayHelpers.arbHumanizeRawMarketKey(rawMarket)
        : "") ||
      canonicalMarket ||
      rawMarket;
    market = looksLikeRawArbMarketKey(human) ? "" : human;
  }

  const explicitMapToken = buildArbDisplayPeriodScopeToken(item, "esports");
  const map = String(item?.map || item?.mapName || "").trim();

  const parts = [];
  if (market) parts.push(market);
  if (explicitMapToken) {
    const escaped = explicitMapToken.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(^|[^a-z0-9])${escaped}($|[^a-z0-9])`, "i");
    if (!parts.some((p) => re.test(p))) parts.push(explicitMapToken);
  }
  if (map && !parts.some((p) => p.toLowerCase().includes(map.toLowerCase()))) parts.push(map);

  return parts.filter(Boolean).join(" · ") || "—";
}

function normalizeScope(value) {
  const raw = String(value || "")
    .toLowerCase()
    .replace(/[-_\s]+/g, "");
  if (
    raw === "live" ||
    raw === "inplay" ||
    raw === "livegame" ||
    raw === "livenow" ||
    raw === "ingame" ||
    raw === "ongoing"
  ) return "live";
  if (
    raw === "prematch" ||
    raw === "early" ||
    raw === "upcoming" ||
    raw === "scheduled" ||
    raw === "notstarted"
  ) return "prematch";
  return "prematch";
}

function buildMatchLabel(item) {
  const preferredTitle =
    item?.event_name ||
    item?.eventName ||
    item?.match ||
    item?.matchName ||
    item?.fixtureName ||
    item?.name ||
    item?.title ||
    "";
  if (String(preferredTitle).trim()) return String(preferredTitle).trim();

  const home =
    item?.home_team ||
    item?.homeTeamName ||
    item?.homeTeam ||
    item?.teams?.home?.name ||
    item?.event?.homeTeam ||
    item?.participants?.[0]?.name ||
    "";
  const away =
    item?.away_team ||
    item?.awayTeamName ||
    item?.awayTeam ||
    item?.teams?.away?.name ||
    item?.event?.awayTeam ||
    item?.participants?.[1]?.name ||
    "";

  const homeStr = String(home || "").trim();
  const awayStr = String(away || "").trim();
  if (homeStr && awayStr) return `${homeStr} vs ${awayStr}`;
  if (homeStr || awayStr) return homeStr || awayStr;

  const picks = toArray(item?.picks);
  const pickNames = uniq(
    picks
      .map((pick) => String(pick?.sel ?? pick?.selection ?? "").trim())
      .filter(Boolean)
      .filter((name) => !isDrawLikeSelectionTextUi(name))
  );
  if (pickNames.length >= 2) return `${pickNames[0]} vs ${pickNames[1]}`;
  if (pickNames.length === 1) return pickNames[0];
  return "—";
}

function resolveRawPicks(item) {
  const candidates = [
    toArray(item?.picks),
    toArray(item?.legs),
    toArray(item?.selections),
    toArray(item?.bestSelections),
    toArray(item?.outcomes),
  ];
  for (const picks of candidates) {
    if (picks.length > 0) return picks;
  }
  return [];
}

function resolvePickBook(rawPick) {
  return (
    rawPick?.sportsbook ||
    rawPick?.book ||
    rawPick?.bookmaker ||
    rawPick?.bookmakerName ||
    rawPick?.bookName ||
    rawPick?.operator ||
    rawPick?.site ||
    rawPick?.source ||
    ""
  );
}

function resolvePickSelection(rawPick) {
  return (
    rawPick?.selection ||
    rawPick?.sel ||
    rawPick?.selectionName ||
    rawPick?.outcome ||
    rawPick?.label ||
    rawPick?.name ||
    ""
  );
}

function resolvePickOdds(rawPick) {
  return (
    rawPick?.odds ??
    rawPick?.odds_decimal ??
    rawPick?.oddsDecimal ??
    rawPick?.price ??
    rawPick?.decimalOdds ??
    rawPick?.bestOdds ??
    rawPick?.value ??
    0
  );
}

function normalizePick(rawPick) {
  const book = String(resolvePickBook(rawPick) || "").trim();
  const sel = String(resolvePickSelection(rawPick) || "").trim();
  const odds = toNumber(resolvePickOdds(rawPick), 0);

  const out = {
    sel,
    odds,
    book,
  };
  if (rawPick?.oddsTs != null) {
    const ots = Number(rawPick.oddsTs);
    out.oddsTs = Number.isFinite(ots) ? ots : rawPick.oddsTs;
  } else if (rawPick?.timestamp) {
    const parsed = Date.parse(String(rawPick.timestamp));
    if (Number.isFinite(parsed) && !isSuspiciousEpochLikeMs(parsed)) out.oddsTs = parsed;
  }
  if (rawPick?.bookmakerId != null) out.bookmakerId = rawPick.bookmakerId;

  // Preserve leg line fields when present (Middles already does this; needed for consistent pick preview).
  const lineCandidates = [
    rawPick?.verifiedLine,
    rawPick?.line,
    rawPick?.handicap,
    rawPick?.total,
    rawPick?.point,
    rawPick?.lineA,
    rawPick?.lineB,
    rawPick?.overLine,
    rawPick?.underLine,
  ];
  for (let i = 0; i < lineCandidates.length; i += 1) {
    const v = lineCandidates[i];
    if (isMeaningfulLineForCard(v)) {
      out.line = String(v).trim();
      break;
    }
  }

  // Preserve official Sharp leg fields if present (compat hardening).
  if (rawPick?.sportsbook != null) out.sportsbook = rawPick.sportsbook;
  if (rawPick?.selection != null) out.selection = rawPick.selection;
  if (rawPick?.odds_american != null) out.odds_american = rawPick.odds_american;
  if (rawPick?.odds_decimal != null) out.odds_decimal = rawPick.odds_decimal;
  if (rawPick?.implied_probability != null) out.implied_probability = rawPick.implied_probability;
  if (rawPick?.stake_percent != null) out.stake_percent = rawPick.stake_percent;
  if (rawPick?.timestamp != null) out.timestamp = rawPick.timestamp;
  if (rawPick?.external_event_id != null) out.external_event_id = rawPick.external_event_id;
  if (rawPick?.selection_id != null) out.selection_id = rawPick.selection_id;
  if (rawPick?.market_id != null) out.market_id = rawPick.market_id;
  if (rawPick?.selection_type != null) out.selection_type = rawPick.selection_type;
  if (rawPick?.side != null) out.side = rawPick.side;
  if (rawPick?.verifiedLine != null && String(rawPick.verifiedLine).trim() !== "") {
    out.verifiedLine = String(rawPick.verifiedLine).trim();
  }
  if (rawPick?.handicap != null && String(rawPick.handicap).trim() !== "")
    out.handicap = String(rawPick.handicap).trim();
  if (rawPick?.total != null && String(rawPick.total).trim() !== "")
    out.total = String(rawPick.total).trim();
  if (rawPick?.point != null && String(rawPick.point).trim() !== "")
    out.point = String(rawPick.point).trim();
  if (rawPick?.overLine != null && String(rawPick.overLine).trim() !== "")
    out.overLine = String(rawPick.overLine).trim();
  if (rawPick?.underLine != null && String(rawPick.underLine).trim() !== "")
    out.underLine = String(rawPick.underLine).trim();

  return out;
}

function getResponseItems(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.arbitrage)) return payload.arbitrage;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
}

function sortArbitrageItems(items) {
  return toArray(items).slice().sort((a, b) => {
    const profitDiff = toNumber(b?.profitPct, 0) - toNumber(a?.profitPct, 0);
    if (profitDiff !== 0) return profitDiff;

    const confidenceDiff = toNumber(b?.confidenceScore, 0) - toNumber(a?.confidenceScore, 0);
    if (confidenceDiff !== 0) return confidenceDiff;

    const aKey = getItemStableIdentityKey(a);
    const bKey = getItemStableIdentityKey(b);
    return aKey.localeCompare(bKey);
  });
}

function isSuspiciousKickoffDisplayText(s) {
  const t = String(s || "").trim();
  if (!t) return true;
  if (/1970|01\s*Jan|Jan\s*01|01\/01\/1970|1970-01-01|01\.01\.1970/i.test(t)) return true;
  return false;
}

function isUsableKickoffTs(value) {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return false;
  const a = Math.abs(value);
  let ms;
  if (a >= 1e12) ms = value;
  else if (a >= 1e9) ms = Math.round(value * 1000);
  else return false;
  return !isSuspiciousEpochLikeMs(ms);
}

function isSuspiciousKickoffValue(value) {
  return isSuspiciousKickoffDisplayText(String(value ?? ""));
}

function isUsableKickoffString(value) {
  const t = String(value ?? "").trim();
  if (!t) return false;
  return !isSuspiciousKickoffValue(t);
}

function kickoffTsToMs(value) {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null;
  const a = Math.abs(value);
  if (a >= 1e12) return value;
  if (a >= 1e9) return Math.round(value * 1000);
  return null;
}

function resolvePreferredKickoffCandidate(item) {
  const candidates = [
    item?.start_time,
    item?.startTime,
    item?.kickoff,
    item?.kickoffDate,
    item?.event_start_time,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i];
    if (typeof c === "string" && isUsableKickoffString(c)) return c.trim();
    if (typeof c === "number" && isUsableKickoffTs(c)) {
      const ms = kickoffTsToMs(c);
      if (Number.isFinite(ms) && !isSuspiciousEpochLikeMs(ms)) return c;
    }
  }
  return null;
}

function getSortableKickoffMs(item) {
  if (isUsableKickoffTs(item?.kickoffTs)) {
    const a = Math.abs(item.kickoffTs);
    if (a >= 1e12) return item.kickoffTs;
    if (a >= 1e9) return Math.round(item.kickoffTs * 1000);
    return item.kickoffTs;
  }
  const candidates = [
    item?.start_time,
    item?.event_start_time,
    item?.kickoffDate,
    item?.kickoff,
    item?.startTime,
    item?.startsAt,
    item?.eventStart,
    item?.eventStartAt,
    item?.commenceTime,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i];
    if (!isUsableKickoffString(c)) continue;
    const ms = Date.parse(String(c).trim());
    if (Number.isFinite(ms) && !isSuspiciousEpochLikeMs(ms)) return ms;
  }
  return Number.POSITIVE_INFINITY;
}

function getValidItemLastUpdateTsMs(item) {
  const t = item?.lastUpdateTs;
  if (typeof t !== "number" || !Number.isFinite(t) || t <= 0) return null;
  return t;
}

function sortVisibleArbitrageItemsByRealTime(items, scope) {
  const key = String(scope || "").toLowerCase();
  if (key === "live") {
    return toArray(items).slice().sort((a, b) => {
      const ua = getValidItemLastUpdateTsMs(a);
      const ub = getValidItemLastUpdateTsMs(b);
      const aValid = ua != null;
      const bValid = ub != null;
      if (aValid && bValid && ua !== ub) return ub - ua;
      if (aValid && !bValid) return -1;
      if (!aValid && bValid) return 1;

      const profitDiff = toNumber(b?.profitPct, 0) - toNumber(a?.profitPct, 0);
      if (profitDiff !== 0) return profitDiff;

      const confidenceDiff = toNumber(b?.confidenceScore, 0) - toNumber(a?.confidenceScore, 0);
      if (confidenceDiff !== 0) return confidenceDiff;

      const aKey = getItemStableIdentityKey(a);
      const bKey = getItemStableIdentityKey(b);
      return aKey.localeCompare(bKey);
    });
  }
  return toArray(items).slice().sort((a, b) => {
    const ma = getSortableKickoffMs(a);
    const mb = getSortableKickoffMs(b);
    const aValid = Number.isFinite(ma) && ma !== Number.POSITIVE_INFINITY;
    const bValid = Number.isFinite(mb) && mb !== Number.POSITIVE_INFINITY;
    if (aValid && bValid && ma !== mb) return ma - mb;
    if (aValid && !bValid) return -1;
    if (!aValid && bValid) return 1;

    const profitDiff = toNumber(b?.profitPct, 0) - toNumber(a?.profitPct, 0);
    if (profitDiff !== 0) return profitDiff;

    const confidenceDiff = toNumber(b?.confidenceScore, 0) - toNumber(a?.confidenceScore, 0);
    if (confidenceDiff !== 0) return confidenceDiff;

    const aKey = getItemStableIdentityKey(a);
    const bKey = getItemStableIdentityKey(b);
    return aKey.localeCompare(bKey);
  });
}

function getPickStableKey(pick) {
  if (!pick || typeof pick !== "object") return "";
  const book = String(pick.sportsbook || pick.book || "").trim();
  const sel  = String(pick.selection  || pick.sel  || "").trim();
  return `${book}|${sel}`;
}

function annotateArbitrageDirection(prevItem, nextItem) {
  if (!nextItem || typeof nextItem !== "object") return nextItem;
  const prev = prevItem && typeof prevItem === "object" ? prevItem : null;

  let profitDirection = "flat";
  const prevProfit = toNumber(prev?.profitPct ?? prev?.profitPercent, NaN);
  const nextProfit = toNumber(nextItem?.profitPct ?? nextItem?.profitPercent, NaN);
  if (Number.isFinite(prevProfit) && Number.isFinite(nextProfit)) {
    if (nextProfit > prevProfit) profitDirection = "up";
    else if (nextProfit < prevProfit) profitDirection = "down";
  }

  const prevPicks = toArray(prev?.picks);
  const nextPicks = toArray(nextItem?.picks);
  const prevMap = new Map();
  prevPicks.forEach((p) => {
    const key = getPickStableKey(p);
    if (key) prevMap.set(key, p);
  });

  const pickDirections = {};
  nextPicks.forEach((p) => {
    const key = getPickStableKey(p);
    if (!key) return;
    const prevPick = prevMap.get(key);
    let dir = "flat";
    if (prevPick) {
      const prevOdds = toNumber(prevPick.odds, NaN);
      const nextOdds = toNumber(p.odds, NaN);
      if (Number.isFinite(prevOdds) && Number.isFinite(nextOdds)) {
        if (nextOdds > prevOdds) dir = "up";
        else if (nextOdds < prevOdds) dir = "down";
      }
    }
    pickDirections[key] = dir;
  });

  let cardUpdatedAt = null;
  if (Number.isFinite(nextItem?.lastUpdateTs) && nextItem.lastUpdateTs > 0) {
    cardUpdatedAt = nextItem.lastUpdateTs;
  }

  return {
    ...nextItem,
    profitDirection,
    pickDirections,
    ...(cardUpdatedAt != null ? { cardUpdatedAt } : {}),
  };
}

function mergeArbitrageDisplayMetadata(prevItem, nextItem) {
  if (!nextItem || typeof nextItem !== "object") return nextItem;
  const prev = prevItem && typeof prevItem === "object" ? prevItem : null;
  if (!prev) return nextItem;

  const merged = { ...nextItem };
  const eventName =
    merged?.event_name ||
    merged?.eventName ||
    prev?.event_name ||
    prev?.eventName ||
    merged?.match ||
    prev?.match ||
    "";
  if (String(eventName).trim()) {
    const eventNameStr = String(eventName).trim();
    merged.event_name = eventNameStr;
    merged.eventName = eventNameStr;
  }

  const homeTeam = merged?.home_team || merged?.homeTeamName || prev?.home_team || prev?.homeTeamName || "";
  const awayTeam = merged?.away_team || merged?.awayTeamName || prev?.away_team || prev?.awayTeamName || "";
  if (String(homeTeam).trim()) {
    const homeTeamStr = String(homeTeam).trim();
    merged.home_team = homeTeamStr;
    merged.homeTeamName = homeTeamStr;
  }
  if (String(awayTeam).trim()) {
    const awayTeamStr = String(awayTeam).trim();
    merged.away_team = awayTeamStr;
    merged.awayTeamName = awayTeamStr;
  }

  const timeRaw =
    merged?.start_time ??
    merged?.startTime ??
    merged?.kickoff ??
    merged?.kickoffDate ??
    merged?.event_start_time ??
    prev?.start_time ??
    prev?.startTime ??
    prev?.kickoff ??
    prev?.kickoffDate ??
    prev?.event_start_time ??
    null;
  const hasTimeRaw =
    (typeof timeRaw === "string" && String(timeRaw).trim() !== "") ||
    (typeof timeRaw === "number" && Number.isFinite(timeRaw) && timeRaw > 0);
  if (hasTimeRaw) {
    merged.start_time = timeRaw;
    merged.startTime = timeRaw;
    if (merged.kickoff == null || String(merged.kickoff).trim() === "") merged.kickoff = timeRaw;
    if (merged.kickoffDate == null || String(merged.kickoffDate).trim() === "") merged.kickoffDate = timeRaw;
  }

  if (!String(merged?.match || "").trim()) merged.match = buildMatchLabel(merged);
  return merged;
}

function isArbV2SourcePreviewPayload(payload) {
  if (!payload || typeof payload !== "object") return false;
  const mode = String(payload?.mode || "").trim().toLowerCase();
  if (mode === "source") return true;
  if (payload?._arbV2SourcePreview === true) return true;
  if (payload?.debug && payload.debug.sourcePreview === true) return true;
  return false;
}

function isArbV2SourcePreviewRow(item) {
  if (!item || typeof item !== "object") return false;
  if (item.arbSourcePreview === true || item.arbSourceRow === true) return true;
  const vStatus = String(item.arbDisplayVerificationStatus || "").trim().toLowerCase();
  if (vStatus === "source_only") return true;
  const vLabel = String(item.arbDisplayVerificationLabel || "").trim().toLowerCase();
  if (vLabel === "source only") return true;
  const mode = String(item.arbV2Mode || "").trim().toLowerCase();
  if (mode === "source" || mode === "source_fallback") return true;
  return false;
}

function filterVisibleItems(items, sport, scope, profitFilter, options = {}) {
  const sourcePreviewMode = options?.sourcePreviewMode === true;
  const activeSportKey = mapSportTabToCanonicalSportKey(sport);
  const minProfit = Number(profitFilter || 0);
  const source = toArray(items);
  const scoped = source
    .filter((item) => itemMatchesArbSportTab(item, sport))
    .filter((item) => {
      if (scope === "all") return true;
      return normalizeScope(item?.scope) === scope;
    })
    .filter((item) => Number(item?.profitPct || 0) >= minProfit);

  if (process.env.NODE_ENV !== "production") {
    source.slice(0, 5).forEach((item, idx) => {
      const rowSportKey = getArbSportFilterKey(item);
      const sportVisible = itemMatchesArbSportTab(item, sport);
      const scopeVisible = scope === "all" ? true : normalizeScope(item?.scope) === scope;
      const profitVisible = Number(item?.profitPct || 0) >= minProfit;
      const visible = sportVisible && scopeVisible && profitVisible;
      console.log("[Arb Filter Audit]", {
        idx,
        rawSport: item?.sport ?? item?.sportKey ?? item?.sportName ?? "",
        rawLeague: item?.league ?? item?.leagueName ?? "",
        sportKey: rowSportKey,
        activeSportFilter: activeSportKey,
        rawScope: item?.scope,
        resolvedScope: normalizeScope(item?.scope),
        activeScope: scope,
        scopeVisible,
        isLiveFromStatus: (getAnalysisStatusLabel(item, {}, "en-GB", "", null) || {}).isLive,
        sourcePreviewMode,
        visible,
      });
    });
  }

  return sortVisibleArbitrageItemsByRealTime(scoped, scope);
}

function normalizeArbitrageItem(item, fallbackScope) {
  const rawPicks = resolveRawPicks(item);
  const picks = rawPicks
    .map((pick) => normalizePick(pick))
    .filter((pick) => pick.book || pick.sel || pick.odds);

  const sport =
    item?.sport ||
    item?.sportKey ||
    item?.sportName ||
    item?.category ||
    item?.gameType ||
    "";

  const fxLeague = item?.fixture?.league;
  const fx = item?.fixture || {};
  const info = item?.info || item?.core || item?.meta || fx.info || fx.core || fx.meta;
  const ev = item?.event || item?.fixture?.event;
  const evLeagueStr =
    (ev &&
      (typeof ev.leagueName === "string"
        ? ev.leagueName.trim()
        : typeof ev.league === "string"
          ? ev.league.trim()
          : ev.league && typeof ev.league === "object"
            ? String(ev.league.name || ev.league["@name"] || "").trim()
            : "")) ||
    (typeof ev?.competition === "string" ? ev.competition.trim() : "") ||
    (typeof ev?.competitionName === "string" ? ev.competitionName.trim() : "") ||
    (typeof ev?.tournamentName === "string" ? ev.tournamentName.trim() : "");
  const leagueObjStr =
    leagueStringFromUnknown(item?.league) ||
    leagueStringFromUnknown(fxLeague) ||
    leagueStringFromUnknown(info?.league) ||
    leagueStringFromUnknown(info?.competition) ||
    leagueStringFromUnknown(info?.tournament);
  const league =
    leagueObjStr ||
    item?.leagueName ||
    item?.competition ||
    item?.competitionName ||
    item?.tournament ||
    item?.tournamentName ||
    item?._sourceLeague ||
    (typeof fxLeague === "string" ? fxLeague : "") ||
    (fxLeague && typeof fxLeague === "object"
      ? String(fxLeague.name || fxLeague["@name"] || "").trim()
      : "") ||
    item?.group?.name ||
    item?.division?.name ||
    leagueStringFromUnknown(item?.group) ||
    leagueStringFromUnknown(item?.division) ||
    item?.fixture?.leagueName ||
    item?.fixture?.competition ||
    item?.fixture?.competitionName ||
    item?.fixture?.tournamentName ||
    leagueStringFromUnknown(fx.group) ||
    leagueStringFromUnknown(fx.division) ||
    item?.category?.name ||
    item?.event?.leagueName ||
    evLeagueStr ||
    "";

  const usableKickoffTs = isUsableKickoffTs(item?.kickoffTs);
  const preferredKickoff = resolvePreferredKickoffCandidate(item);

  let kickoffDateStr = "";
  if (typeof preferredKickoff === "string" && isUsableKickoffString(preferredKickoff)) {
    kickoffDateStr = preferredKickoff.trim();
  } else if (typeof preferredKickoff === "number" && isUsableKickoffTs(preferredKickoff)) {
    const ms = kickoffTsToMs(preferredKickoff);
    if (Number.isFinite(ms) && !isSuspiciousEpochLikeMs(ms)) {
      try {
        kickoffDateStr = new Date(ms).toISOString();
      } catch {
        kickoffDateStr = "";
      }
    }
  } else if (usableKickoffTs) {
    const ms = kickoffTsToMs(item.kickoffTs);
    if (Number.isFinite(ms) && !isSuspiciousEpochLikeMs(ms)) {
      try {
        kickoffDateStr = new Date(ms).toISOString();
      } catch {
        kickoffDateStr = "";
      }
    }
  } else if (typeof item?.kickoff === "string" && isUsableKickoffString(item.kickoff)) {
    kickoffDateStr = item.kickoff.trim();
  } else {
    const fx = item?.fixture || {};
    const ev = item?.event || fx.event || {};
    const fallbackKo =
      item?.startTime ??
      item?.startsAt ??
      item?.eventStart ??
      item?.eventStartAt ??
      item?.commenceTime ??
      item?.matchTime ??
      item?.scheduled ??
      fx.date ??
      fx.dateTime ??
      fx.datetime ??
      fx.kickoff ??
      fx.kickoffDate ??
      fx.kickoffTs ??
      fx.startsAt ??
      fx.startTime ??
      ev.dateTime ??
      ev.datetime ??
      ev.startsAt ??
      ev.startTime ??
      ev.scheduled ??
      "";
    if (fallbackKo != null && fallbackKo !== "") {
      const fs = String(fallbackKo);
      if (isUsableKickoffString(fs)) kickoffDateStr = fs;
    }
  }

  const market =
    item?.market_type ||
    item?.market ||
    item?.marketType ||
    item?.marketName ||
    item?.betType ||
    item?.lineLabel ||
    "";

  const line = item?.line ?? item?.point ?? item?.handicap ?? item?.total ?? "";
  const map = item?.map || item?.mapName || item?.round || "";
  const game = item?.game || item?.gameName || item?.esportGame || "";

  const id =
    item?.id ||
    item?.arbId ||
    item?.eventId ||
    item?.fixtureId ||
    item?.matchId ||
    `${buildMatchLabel(item)}-${market}-${line}-${normalizeScope(item?.scope || fallbackScope)}`;

  const profitPct = toNumber(
    item?.profit_percent ?? item?.profitPct ?? item?.profitPercent ?? item?.roiPct ?? item?.roiPercent,
    0
  );

  const confidenceScore = toNumber(item?.confidenceScore ?? item?.confidence ?? 0, 0);

  const matchLabel = buildMatchLabel(item);
  const eventName = String(item?.event_name || item?.eventName || matchLabel || "").trim();
  const homeTeam = String(item?.home_team || item?.homeTeamName || "").trim();
  const awayTeam = String(item?.away_team || item?.awayTeamName || "").trim();
  const startTimeRaw =
    item?.start_time ??
    item?.startTime ??
    item?.kickoff ??
    item?.kickoffDate ??
    item?.event_start_time ??
    kickoffDateStr;
  const hasStartTimeRaw =
    (typeof startTimeRaw === "string" && String(startTimeRaw).trim() !== "") ||
    (typeof startTimeRaw === "number" && Number.isFinite(startTimeRaw) && startTimeRaw > 0);
  let kickoffValue = kickoffDateStr;
  if (!kickoffValue && typeof startTimeRaw === "string" && isUsableKickoffString(startTimeRaw)) {
    kickoffValue = startTimeRaw.trim();
  } else if (!kickoffValue && typeof startTimeRaw === "number" && isUsableKickoffTs(startTimeRaw)) {
    const ms = kickoffTsToMs(startTimeRaw);
    if (Number.isFinite(ms) && !isSuspiciousEpochLikeMs(ms)) {
      try {
        kickoffValue = new Date(ms).toISOString();
      } catch {
        kickoffValue = "";
      }
    }
  }
  if (!kickoffValue && typeof item?.kickoffDate === "string" && item.kickoffDate.trim()) {
    kickoffValue = item.kickoffDate.trim();
  } else if (!kickoffValue && typeof item?.kickoff === "string" && item.kickoff.trim()) {
    kickoffValue = item.kickoff.trim();
  }

  // Use getAnalysisStatusLabel as primary source of truth —
  // same logic that drives the LIVE badge on each card.
  const rawStatusInfo =
    getAnalysisStatusLabel(item, {}, "en-GB", "", null) || {};
  const isLiveFromStatusDetection = rawStatusInfo.isLive === true;

  // Secondary: explicit field signals on the raw row.
  const isLiveFromWarnings =
    Array.isArray(item?.warnings) &&
    item.warnings.includes("LIVE_GAME");
  const isLiveFromFields =
    item?.is_live === true ||
    item?.isLive === true ||
    isLiveFromWarnings;

  // Tertiary: raw scope string from server
  // (unreliable — server uses "live" to mean feed type, not
  //  game status, so only trust it for "early"/"prematch").
  const rawScopeStr =
    item?.scope != null && item?.scope !== ""
      ? String(item.scope).toLowerCase().replace(/[-_\s]+/g, "")
      : "";
  const scopeFieldIsPrematch =
    rawScopeStr === "early" ||
    rawScopeStr === "prematch" ||
    rawScopeStr === "upcoming" ||
    rawScopeStr === "scheduled" ||
    rawScopeStr === "notstarted";

  const scopeResolved =
    (isLiveFromStatusDetection || isLiveFromFields) &&
    !scopeFieldIsPrematch
      ? "live"
      : "prematch";

  const evTz = item?.event || item?.fixture?.event;
  const timeZoneResolved =
    (typeof item?.timeZone === "string" && item.timeZone.trim()) ||
    (typeof item?.timezone === "string" && item.timezone.trim()) ||
    (typeof item?.tz === "string" && item.tz.trim()) ||
    (item?.fixture && item.fixture.timezone) ||
    (item?.fixture && item.fixture.timeZone) ||
    (item?.fixture && item.fixture.tz) ||
    (item?.event && item.event.timeZone) ||
    (item?.event && item.event.timezone) ||
    (evTz && typeof evTz.timeZone === "string" && evTz.timeZone.trim()) ||
    (evTz && typeof evTz.timezone === "string" && evTz.timezone.trim()) ||
    (evTz && typeof evTz.tz === "string" && evTz.tz.trim()) ||
    "Asia/Ho_Chi_Minh";

  const next = {
    ...item,
    id: String(id),
    scope: scopeResolved,
    sport: String(sport),
    league: String(league || "").trim(),
    event_name: eventName,
    eventName,
    ...(homeTeam ? { home_team: homeTeam, homeTeamName: homeTeam } : {}),
    ...(awayTeam ? { away_team: awayTeam, awayTeamName: awayTeam } : {}),
    ...(hasStartTimeRaw ? { start_time: startTimeRaw, startTime: startTimeRaw } : {}),
    kickoffDate: kickoffValue,
    kickoff: kickoffValue,
    match: matchLabel,
    profitPct,
    confidenceScore,
    market: String(market || "").trim(),
    line: line == null ? "" : String(line),
    map: String(map || "").trim(),
    game: String(game || "").trim(),
    picks,
    possibly_stale: item?.possibly_stale ?? false,
    warnings: Array.isArray(item?.warnings) ? item.warnings : [],
  };

  if (usableKickoffTs) {
    next.kickoffTs = item.kickoffTs;
  } else {
    delete next.kickoffTs;
  }

  if (timeZoneResolved) {
    next.timeZone = String(timeZoneResolved);
  }

  return next;
}

/* --------------------
COMPONENT
-------------------- */
export default function ArbitragePage({ active = true, lang = "en-GB", timeZone, oddsFormat = "hk" }) {
  const t = useMemo(() => getAnalysisTranslations(lang), [lang]);
  const [scope, setScope] = useState("all");
  const [sport, setSport] = useState("football");

  const [profitFilter, setProfitFilter] = useState(0.5);
  const [isProfitOpen, setIsProfitOpen] = useState(false);

  // Ensure profitFilter always stays within the shared dropdown option list.
  useEffect(() => {
    setProfitFilter((prev) => {
      const next = clampToNearestNumberOption(prev, PROFIT_OPTIONS);
      return next === prev ? prev : next;
    });
  }, []);

  const [autoRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [lastScanAt, setLastScanAt] = useState(0);
  const [results, setResults] = useState([]);
  const [sportPool, setSportPool] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [lastVisibleArbitragePublishedAt, setLastVisibleArbitragePublishedAt] = useState(0);
  const [lastGoalserveWsError, setLastGoalserveWsError] = useState(null);
  const [lastArbV2PayloadCount, setLastArbV2PayloadCount] = useState(0);

  const pollTimerRef = useRef(null);
  const abortPollRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const latestRawArbitrageRef = useRef([]);
  const latestServerStatusRef = useRef(null);
  const fullPoolRef = useRef([]);
  const hasReceivedArbitrageListRef = useRef(false);
  const scopeRef = useRef(scope);
  const sportRef = useRef(sport);
  const profitFilterRef = useRef(profitFilter);
  const autoRefreshRef = useRef(autoRefresh);
  const activeRef = useRef(active);
  const profitDropdownRef = useRef(null);
  const analysisWsRef = useRef(null);
  const analysisWsReconnectTimerRef = useRef(null);
  const analysisWsAttemptRef = useRef(0);
  const pushTransportActiveRef = useRef(false);
  const arbV2SourcePreviewActiveRef = useRef(false);
  const v2SourceZeroPollStreakRef = useRef(0);
  const ARB_V2_MAX_EMPTY_RETAIN_STREAK = 3;
  const lastWsArbV2SourceAtRef = useRef(0);
  const lastArbV2WsPayloadAtRef = useRef(0);
  const prevLoadingRef = useRef(loading);
  const [displayResults, setDisplayResults] = useState([]);
  const leavingTimersRef = useRef(new Map());
  const fadeTimersRef    = useRef(new Map());
  const [leavingKeys, setLeavingKeys]  = useState(new Set());
  const prevResultKeysRef = useRef(new Set());
  const prevScopeRef = useRef(scope);
  const prevSportRef = useRef(sport);

  const scopeTabs = useMemo(
    () => [
      { key: "all", label: t.common.all },
      { key: "live", label: t.common.live },
      { key: "prematch", label: t.common.prematch },
    ],
    [t]
  );

  const activeIndex = useMemo(() => {
    const idx = scopeTabs.findIndex((tab) => tab.key === scope);
    return idx >= 0 ? idx : 0;
  }, [scope, scopeTabs]);

  const indicatorWidth = 100 / scopeTabs.length;

  const sportTabs = useMemo(
    () => [
      { key: "football", label: `⚽ ${t.sports.football}` },
      { key: "tennis", label: `🎾 ${t.sports.tennis}` },
      { key: "basketball", label: `🏀 ${t.sports.basketball}` },
      { key: "esports", label: `🎮 ${t.sports.esports}` },
    ],
    [t]
  );

  const hasBackendError = Boolean(lastGoalserveWsError);
  const hasRealtimeData = sportPool.length > 0;
  const sportBadgeCounts = useMemo(() => {
    const minProfit = Number(profitFilter || 0);
    const counts = {
      all: 0,
      soccer: 0,
      tennis: 0,
      basketball: 0,
      esports: 0,
    };
    const pool = toArray(sportPool);
    for (const item of pool) {
      if (scope !== "all" && normalizeScope(item?.scope) !== scope) continue;
      if (Number(item?.profitPct || 0) < minProfit) continue;
      const sportKey = getArbSportFilterKey(item);
      counts.all += 1;
      if (sportKey === "soccer") counts.soccer += 1;
      else if (sportKey === "tennis") counts.tennis += 1;
      else if (sportKey === "basketball") counts.basketball += 1;
      else if (sportKey === "esports") counts.esports += 1;
    }
    return counts;
  }, [sportPool, scope, profitFilter]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    console.log("[ARB SPORT BADGES]", {
      activeSportFilter: mapSportTabToCanonicalSportKey(sport),
      sportBadgeCounts,
      totalPoolRows: toArray(sportPool).length,
      visibleRows: results.length,
    });
  }, [sport, sportBadgeCounts, sportPool, results.length]);

  const applyVisibleBatchMetaFromStatus = useCallback((st) => {
    if (!st || typeof st !== "object") return;
    const pub = Number(st.lastArbitragePublishedAt);
    if (Number.isFinite(pub) && pub > 0) setLastVisibleArbitragePublishedAt(pub);
  }, []);

  const applyArbitragePayload = useCallback((rawPayload, options = {}) => {
    const {
      allowEmptyReplace = false,
      keepNonEmptyOnTransientEmpty = true,
      sourcePreviewMode: sourcePreviewModeOption = false,
    } = options;
    const FALLBACK_SCOPE = "prematch";
    const sourceItems = getResponseItems(rawPayload).filter(
      (item) => !(item && typeof item === "object" && item.shouldSurface === false)
    );
    const sourcePreviewMode =
      sourcePreviewModeOption === true ||
      isArbV2SourcePreviewPayload(rawPayload) ||
      sourceItems.some((item) => isArbV2SourcePreviewRow(item));
    arbV2SourcePreviewActiveRef.current = sourcePreviewMode;

    if (!sourceItems.length) {
      if (fullPoolRef.current.length > 0 && (keepNonEmptyOnTransientEmpty || !allowEmptyReplace)) {
        const stRetain = latestServerStatusRef.current;
        applyVisibleBatchMetaFromStatus(stRetain);
        const visibleTs = pickVisibleScanTs(latestServerStatusRef.current) || Date.now();
        setLastScanAt(visibleTs);
        setLoading(false);
        return;
      }

      const fullPool = [];
      const selectedScope = scopeRef.current;
      const selectedSport = sportRef.current;
      const selectedProfit = profitFilterRef.current;
      const visibleData = filterVisibleItems(
        fullPool,
        selectedSport,
        selectedScope,
        selectedProfit,
        { sourcePreviewMode }
      );

      fullPoolRef.current = fullPool;
      setSportPool((prev) => {
        if (areArbitrageCollectionsEquivalent(prev, fullPool)) return prev;
        return fullPool;
      });
      setResults((prev) => {
        if (areArbitrageCollectionsEquivalent(prev, visibleData)) return prev;
        return visibleData;
      });

      const stEmpty = latestServerStatusRef.current;
      applyVisibleBatchMetaFromStatus(stEmpty);
      const visibleTs = pickVisibleScanTs(latestServerStatusRef.current) || Date.now();
      setLastScanAt(visibleTs);
      setLoading(false);
      return;
    }

    const normalizedData = sourceItems
      .map((item) => normalizeArbitrageItem(item, FALLBACK_SCOPE))
      .filter(Boolean)
      .filter((item) => getItemStableIdentityKey(item));

    const prevPoolById = new Map();
    toArray(fullPoolRef.current).forEach((it) => {
      const k = getItemStableIdentityKey(it);
      if (k) prevPoolById.set(k, it);
    });

    const dedupedMap = new Map();
    normalizedData.forEach((item) => {
      const stableId = getItemStableIdentityKey(item);
      if (!stableId) return;
      const prev = dedupedMap.get(stableId) || prevPoolById.get(stableId) || null;
      const mergedMeta = mergeArbitrageDisplayMetadata(prev, item);
      const annotated = annotateArbitrageDirection(prev, mergedMeta);
      dedupedMap.set(stableId, annotated);
    });

    const dedupedData = Array.from(dedupedMap.values());
    const fullPool = sortArbitrageItems(dedupedData);

    const selectedScope = scopeRef.current;
    const selectedSport = sportRef.current;
    const selectedProfit = profitFilterRef.current;
    const visibleData = filterVisibleItems(
      fullPool,
      selectedSport,
      selectedScope,
      selectedProfit,
      { sourcePreviewMode }
    );
    fullPoolRef.current = fullPool;
    setSportPool((prev) => {
      if (areArbitrageCollectionsEquivalent(prev, fullPool)) return prev;
      return fullPool;
    });
    setResults((prev) => {
      if (areArbitrageCollectionsEquivalent(prev, visibleData)) return prev;
      return visibleData;
    });

    const stDone = latestServerStatusRef.current;
    applyVisibleBatchMetaFromStatus(stDone);
    const visibleTs = pickVisibleScanTs(latestServerStatusRef.current) || Date.now();
    setLastScanAt(visibleTs);
    setLoading(false);
  }, [applyVisibleBatchMetaFromStatus]);

  useEffect(() => {
    scopeRef.current = scope;
    sportRef.current = sport;
    profitFilterRef.current = profitFilter;
    autoRefreshRef.current = autoRefresh;
    activeRef.current = active;
  }, [scope, sport, profitFilter, autoRefresh, active]);

  useEffect(() => {
    let currentRawItems = getResponseItems(latestRawArbitrageRef.current);
    if (!currentRawItems.length && fullPoolRef.current.length > 0) {
      currentRawItems = fullPoolRef.current;
    }
    currentRawItems = toArray(currentRawItems).filter(
      (item) => !(item && typeof item === "object" && item.shouldSurface === false)
    );

    if (!currentRawItems.length) {
      const pool = fullPoolRef.current;
      if (pool.length > 0) {
        const visible = filterVisibleItems(pool, sport, scope, profitFilter, {
          sourcePreviewMode: arbV2SourcePreviewActiveRef.current,
        });
        setResults((prev) => {
          if (areArbitrageCollectionsEquivalent(prev, visible)) return prev;
          return visible;
        });
        return;
      }
      const visible = filterVisibleItems([], sport, scope, profitFilter, {
        sourcePreviewMode: arbV2SourcePreviewActiveRef.current,
      });
      setResults((prev) => {
        if (areArbitrageCollectionsEquivalent(prev, visible)) return prev;
        return visible;
      });
      return;
    }

    const rebuiltPool = currentRawItems
      .map((item) => normalizeArbitrageItem(item, "prematch"))
      .filter(Boolean)
      .filter((item) => getItemStableIdentityKey(item));

    const prevPoolById = new Map();
    toArray(fullPoolRef.current).forEach((it) => {
      const k = getItemStableIdentityKey(it);
      if (k) prevPoolById.set(k, it);
    });

    const dedupedMap = new Map();
    rebuiltPool.forEach((item) => {
      const stableId = getItemStableIdentityKey(item);
      if (!stableId) return;
      const prev = dedupedMap.get(stableId) || prevPoolById.get(stableId) || null;
      const mergedMeta = mergeArbitrageDisplayMetadata(prev, item);
      const annotated = annotateArbitrageDirection(prev, mergedMeta);
      dedupedMap.set(stableId, annotated);
    });
    const fullPool = sortArbitrageItems(Array.from(dedupedMap.values()));
    const visibleData = filterVisibleItems(fullPool, sport, scope, profitFilter, {
      sourcePreviewMode: arbV2SourcePreviewActiveRef.current,
    });
    fullPoolRef.current = fullPool;
    setSportPool((prev) => {
      if (areArbitrageCollectionsEquivalent(prev, fullPool)) return prev;
      return fullPool;
    });
    setResults((prev) => {
      if (areArbitrageCollectionsEquivalent(prev, visibleData)) return prev;
      return visibleData;
    });
  }, [scope, sport, profitFilter]);

  useEffect(() => {
    if (autoRefresh) {
      applyArbitragePayload(latestRawArbitrageRef.current, { allowEmptyReplace: false });
    }
  }, [autoRefresh, applyArbitragePayload]);

  useEffect(() => {
    if (!ARB_V2_PRODUCTION_MODE_ENABLED || process.env.NODE_ENV === "production") return;
    if (prevLoadingRef.current === loading) return;
    prevLoadingRef.current = loading;
    const lastWsPayloadAt = Number(lastArbV2WsPayloadAtRef.current || 0);
    const lastWsPayloadAgeMs = lastWsPayloadAt > 0 ? Date.now() - lastWsPayloadAt : null;
    console.log("[ARB V2 UI STATUS]", {
      scanning: loading,
      wsPrimary: pushTransportActiveRef.current === true,
      lastWsPayloadAgeMs,
      visibleRows: results.length,
      lastPayloadCount: lastArbV2PayloadCount,
      mode: "production",
    });
  }, [loading, results.length, lastArbV2PayloadCount]);

  useEffect(() => {
    if (!active) {
      pushTransportActiveRef.current = false;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      if (abortPollRef.current) {
        abortPollRef.current.abort();
        abortPollRef.current = null;
      }
      if (analysisWsReconnectTimerRef.current) {
        clearTimeout(analysisWsReconnectTimerRef.current);
        analysisWsReconnectTimerRef.current = null;
      }
      if (analysisWsRef.current) {
        try {
          analysisWsRef.current.close();
        } catch {
          /* ignore */
        }
        analysisWsRef.current = null;
      }
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const clearPoll = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const clearWsReconnect = () => {
      if (analysisWsReconnectTimerRef.current) {
        clearTimeout(analysisWsReconnectTimerRef.current);
        analysisWsReconnectTimerRef.current = null;
      }
    };

    const scheduleWsReconnect = () => {
      clearWsReconnect();
      const attempt = analysisWsAttemptRef.current;
      const delayMs = Math.min(30000, Math.round(1000 * Math.pow(1.5, attempt)));
      analysisWsReconnectTimerRef.current = setTimeout(() => {
        analysisWsReconnectTimerRef.current = null;
        connectAnalysisWs();
      }, delayMs);
    };

    const scheduleRestPoll = () => {
      clearPoll();
      pollTimerRef.current = setInterval(() => {
        restTick();
      }, ARB_V2_SOURCE_POLL_MS);
    };

    const restTick = async () => {
      if (!activeRef.current || pollInFlightRef.current) return;
      // WebSocket is the primary transport for Arb V2 production. REST is
      // only used as bootstrap (no WS payload yet) or fallback (WS down or
      // silent for > 20s). When WS is connected AND a production payload
      // arrived recently we skip REST entirely so the UI does not appear
      // to poll every 8s.
      const wsConnectedNow = pushTransportActiveRef.current === true;
      const lastWsAt = Math.max(
        Number(lastWsArbV2SourceAtRef.current || 0),
        Number(lastArbV2WsPayloadAtRef.current || 0)
      );
      const sinceWs = Date.now() - lastWsAt;
      const wsRecent = lastWsAt > 0 && sinceWs < 20000;
      if (wsConnectedNow && wsRecent) {
        return;
      }
      pollInFlightRef.current = true;
      if (abortPollRef.current) abortPollRef.current.abort();
      const ac = new AbortController();
      abortPollRef.current = ac;
      setLoading(true);
      try {
        const base = await detectAnalysisApiBase();
        const mode = getArbV2RequestMode();
        const res = await fetch(`${base}/api/debug/arbitrage-v2?scope=all&mode=${mode}`, {
          signal: ac.signal,
        });
        if (!res.ok) throw new Error("http");
        const j = await res.json();
        if (!j || j.ok !== true) throw new Error("json");
        const rows = Array.isArray(j.results) ? j.results : [];
        setLastArbV2PayloadCount(rows.length);
        const payloadMode = String(j.publishMode || j.mode || "").trim().toLowerCase();
        const sourcePreviewMode = ARB_V2_SOURCE_PREVIEW_ENABLED || payloadMode === "source";
        if (rows.length === 0 && toArray(fullPoolRef.current).length > 0) {
          v2SourceZeroPollStreakRef.current += 1;
          if (v2SourceZeroPollStreakRef.current < ARB_V2_MAX_EMPTY_RETAIN_STREAK) {
            if (process.env.NODE_ENV !== "production") {
              console.log("[ARB V2 SOURCE UI] keep previous pool on transient empty", {
                zeroStreak: v2SourceZeroPollStreakRef.current,
                maxRetainStreak: ARB_V2_MAX_EMPTY_RETAIN_STREAK,
              });
            }
            setLoading(false);
            return;
          }
          if (process.env.NODE_ENV !== "production") {
            console.log("[ARB V2 SOURCE UI] empty streak reached threshold; clearing pool (REST)", {
              zeroStreak: v2SourceZeroPollStreakRef.current,
              maxRetainStreak: ARB_V2_MAX_EMPTY_RETAIN_STREAK,
            });
          }
        } else {
          v2SourceZeroPollStreakRef.current = 0;
        }
        const payload = rows.map((row) => normalizeIncomingArbV2Row(row, { sourcePreviewMode }));
        const payloadEnvelope = {
          ...j,
          mode: sourcePreviewMode ? "source" : "production",
          publishMode: sourcePreviewMode ? "source" : "production",
          _arbV2SourcePreview: sourcePreviewMode,
          results: payload,
        };
        const updatedAt = Number(j.updatedAt || Date.now()) || Date.now();

        // Do not let a slower REST response overwrite a newer WS payload.
        if (
          Math.max(
            Number(lastWsArbV2SourceAtRef.current || 0),
            Number(lastArbV2WsPayloadAtRef.current || 0)
          ) > 0 &&
          updatedAt <
            Math.max(
              Number(lastWsArbV2SourceAtRef.current || 0),
              Number(lastArbV2WsPayloadAtRef.current || 0)
            )
        ) {
          if (process.env.NODE_ENV !== "production") {
            console.log("[ARB V2 SOURCE UI] skip REST overwrite of newer WS payload");
          }
          setLoading(false);
          return;
        }

        if (process.env.NODE_ENV !== "production") {
          console.log("[ARB V2 PRODUCTION REST FALLBACK]", {
            apiCount: Number(j?.count || 0),
            resultRows: rows.length,
            mode: j?.mode,
            publishMode: j?.publishMode,
            sourceRows: j?.sourceRows,
            afterCommercialRows: j?.afterCommercialRows,
            commercialRejectedRows: j?.commercialRejectedRows,
            productionRows: j?.productionRows,
            publishedCount: j?.publishedCount,
            updatedAt: j?.updatedAt,
            wsConnected: pushTransportActiveRef.current === true,
            sinceWsMs: lastWsArbV2SourceAtRef.current
              ? Date.now() - lastWsArbV2SourceAtRef.current
              : null,
          });
        }

        latestRawArbitrageRef.current = payloadEnvelope;
        hasReceivedArbitrageListRef.current = true;
        latestServerStatusRef.current = {
          lastArbitragePublishedAt: updatedAt,
          lastPollFinishedAt: Date.now(),
        };
        setLastScanAt(updatedAt);
        setLastVisibleArbitragePublishedAt(updatedAt);
        setWsConnected(true);
        setLastGoalserveWsError(null);
        applyVisibleBatchMetaFromStatus(latestServerStatusRef.current);
        if (autoRefreshRef.current) {
          applyArbitragePayload(payloadEnvelope, {
            allowEmptyReplace: true,
            keepNonEmptyOnTransientEmpty: false,
            sourcePreviewMode,
          });
        } else {
          setLoading(false);
        }
      } catch (e) {
        if (e?.name === "AbortError") return;
        setWsConnected(false);
        setLastGoalserveWsError({ message: "API unreachable", at: Date.now() });
      } finally {
        pollInFlightRef.current = false;
        setLoading(false);
      }
    };

    const connectAnalysisWs = async () => {
      if (cancelled || !activeRef.current) return;
      const base = await detectAnalysisApiBase();
      const wsUrl = httpBaseToAnalysisWsUrl(base);
      if (!wsUrl || typeof WebSocket === "undefined") {
        pushTransportActiveRef.current = false;
        analysisWsAttemptRef.current = Math.min(analysisWsAttemptRef.current + 1, 20);
        await restTick();
        scheduleRestPoll();
        scheduleWsReconnect();
        return;
      }
      try {
        const ws = new WebSocket(wsUrl);
        analysisWsRef.current = ws;
        ws.onopen = () => {
          if (cancelled || analysisWsRef.current !== ws) return;
          analysisWsAttemptRef.current = 0;
          pushTransportActiveRef.current = true;
          clearWsReconnect();
        };
        ws.onmessage = (ev) => {
          if (cancelled || analysisWsRef.current !== ws) return;
          let msg;
          try {
            msg = JSON.parse(ev.data);
          } catch {
            return;
          }
          if (!msg || typeof msg !== "object") return;
          if (msg.type === "server_status" && msg.payload) {
            const meta = mapAnalysisServerStatusToArbMeta(msg.payload);
            if (meta) {
              latestServerStatusRef.current = meta;
              applyVisibleBatchMetaFromStatus(meta);
              const pub = Number(meta.lastArbitragePublishedAt || 0);
              if (Number.isFinite(pub) && pub > 0) {
                setLastVisibleArbitragePublishedAt(pub);
                setLastScanAt(pub);
              }
            }
          }
          const isArbV2WsPayload =
            (msg.type === "arb_v2_source_list" || msg.type === "arb_v2_commercial_list") &&
            msg.ok === true;
          if (isArbV2WsPayload) {
            if (!autoRefreshRef.current) return;
            const wsMode = String(msg.publishMode || msg.mode || "").trim().toLowerCase();
            const sourcePreviewMode = ARB_V2_SOURCE_PREVIEW_ENABLED || wsMode === "source";
            const productionMode =
              wsMode === "production" || wsMode === "commercial" || wsMode === "verified";
            if (!sourcePreviewMode && !productionMode) return;
            // WS is now the primary transport for this payload. Mark active
            // before any early returns so REST fallback gating sees a fresh
            // WS heartbeat even when the payload is empty/retained.
            pushTransportActiveRef.current = true;
            lastWsArbV2SourceAtRef.current = Date.now();
            lastArbV2WsPayloadAtRef.current = Date.now();
            const rawRows = Array.isArray(msg.results) ? msg.results : [];
            const wsUpdatedAt = Number(msg.updatedAt || 0) || Date.now();
            const retainedLastGood = msg.retainedLastGood === true;
            setLastArbV2PayloadCount(rawRows.length);
            setWsConnected(true);
            setLastGoalserveWsError(null);
            latestServerStatusRef.current = {
              lastArbitragePublishedAt: wsUpdatedAt,
              lastPollFinishedAt: Date.now(),
            };
            setLastScanAt(wsUpdatedAt);
            setLastVisibleArbitragePublishedAt(wsUpdatedAt);
            applyVisibleBatchMetaFromStatus(latestServerStatusRef.current);
            setLoading(false);
            if (process.env.NODE_ENV !== "production" && productionMode) {
              try {
                console.log("[ARB V2 PRODUCTION WS]", {
                  wsCount: rawRows.length,
                  count: msg.count,
                  mode: msg.mode,
                  publishMode: msg.publishMode,
                  sourceRows: msg.sourceRows,
                  afterCommercialRows: msg.afterCommercialRows,
                  commercialRejectedRows: msg.commercialRejectedRows,
                  productionRows: msg.productionRows,
                  publishedCount: msg.publishedCount,
                  updatedAt: msg.updatedAt,
                });
              } catch (_) {
                /* ignore */
              }
            }
            if (rawRows.length === 0 && toArray(fullPoolRef.current).length > 0) {
              v2SourceZeroPollStreakRef.current += 1;
              const belowThreshold = v2SourceZeroPollStreakRef.current < ARB_V2_MAX_EMPTY_RETAIN_STREAK;
              if (belowThreshold && retainedLastGood) {
                if (process.env.NODE_ENV !== "production") {
                  console.log("[ARB V2 SOURCE WS] empty payload, server retained last-good, below threshold; ignoring", {
                    zeroStreak: v2SourceZeroPollStreakRef.current,
                    maxRetainStreak: ARB_V2_MAX_EMPTY_RETAIN_STREAK,
                    consecutiveEmptySourcePolls: msg.consecutiveEmptySourcePolls,
                  });
                }
                return;
              }
              if (belowThreshold) {
                if (process.env.NODE_ENV !== "production") {
                  console.log("[ARB V2 SOURCE WS] keep previous pool on transient empty", {
                    zeroStreak: v2SourceZeroPollStreakRef.current,
                    maxRetainStreak: ARB_V2_MAX_EMPTY_RETAIN_STREAK,
                  });
                }
                return;
              }
              if (process.env.NODE_ENV !== "production") {
                console.log("[ARB V2 SOURCE WS] empty streak reached threshold; clearing pool (WS)", {
                  zeroStreak: v2SourceZeroPollStreakRef.current,
                  maxRetainStreak: ARB_V2_MAX_EMPTY_RETAIN_STREAK,
                });
              }
            } else {
              v2SourceZeroPollStreakRef.current = 0;
            }
            const payload = rawRows.map((row) => normalizeIncomingArbV2Row(row, { sourcePreviewMode }));
            const payloadEnvelope = {
              ok: true,
              version: "arb-v2",
              mode: sourcePreviewMode ? "source" : "production",
              publishMode: sourcePreviewMode ? "source" : "production",
              scope: msg.scope || "all",
              updatedAt: wsUpdatedAt,
              count: payload.length,
              results: payload,
              _arbV2SourcePreview: sourcePreviewMode,
              _arbV2SourceTransport: "ws",
            };
            if (process.env.NODE_ENV !== "production") {
              console.log("[ARB V2 SOURCE WS]", {
                wsCount: Number(msg.count || 0),
                resultRows: rawRows.length,
                updatedAt: wsUpdatedAt,
                retainedLastGood,
              });
            }
            latestRawArbitrageRef.current = payloadEnvelope;
            hasReceivedArbitrageListRef.current = true;
            latestServerStatusRef.current = {
              lastArbitragePublishedAt: wsUpdatedAt,
              lastPollFinishedAt: Date.now(),
            };
            setLastScanAt(wsUpdatedAt);
            setLastVisibleArbitragePublishedAt(wsUpdatedAt);
            setWsConnected(true);
            setLastGoalserveWsError(null);
            applyVisibleBatchMetaFromStatus(latestServerStatusRef.current);
            applyArbitragePayload(payloadEnvelope, {
              allowEmptyReplace: true,
              keepNonEmptyOnTransientEmpty: false,
              sourcePreviewMode,
            });
            return;
          }
          if (msg.type === "arbitrage_list" && msg.ok === true) {
            if (!autoRefreshRef.current) return;
            const merged = Array.isArray(msg.results) ? msg.results : [];
            if (ARB_V2_SOURCE_PREVIEW_ENABLED || ARB_V2_PRODUCTION_MODE_ENABLED) {
              if (process.env.NODE_ENV !== "production") {
                console.log(
                  "[ARB V2 UI] ignored V1/ws arbitrage_list while Arb V2 mode active",
                  { count: merged.length }
                );
              }
              return;
            }
            const currentPool = toArray(fullPoolRef.current);
            const currentCount = currentPool.length;
            const sourcePreviewActive = arbV2SourcePreviewActiveRef.current === true;
            const currentHasSourceRows = currentPool.some((x) => isArbV2SourcePreviewRow(x));
            const incomingHasSourceRows = merged.some((x) => isArbV2SourcePreviewRow(x));
            const shouldIgnoreEmptyIncoming = merged.length === 0 && currentCount > 0;
            const shouldIgnoreSourceRegression = currentHasSourceRows && !incomingHasSourceRows;
            const shouldIgnoreSourcePreviewOverwrite = sourcePreviewActive && !incomingHasSourceRows;
            const shouldIgnoreWsPayload =
              shouldIgnoreEmptyIncoming ||
              shouldIgnoreSourceRegression ||
              shouldIgnoreSourcePreviewOverwrite;

            setWsConnected(true);
            setLastGoalserveWsError(null);
            const uAt = Number(msg.updatedAt || 0);
            if (Number.isFinite(uAt) && uAt > 0) {
              latestServerStatusRef.current = {
                lastArbitragePublishedAt: uAt,
                lastPollFinishedAt: Date.now(),
              };
              setLastScanAt(uAt);
              setLastVisibleArbitragePublishedAt(uAt);
            }
            applyVisibleBatchMetaFromStatus(latestServerStatusRef.current);
            if (shouldIgnoreWsPayload) {
              if (process.env.NODE_ENV !== "production") {
                console.log("[Arb WS] ignored smaller/non-source payload; keeping REST scope=all rows", {
                  incomingCount: merged.length,
                  currentCount,
                  incomingHasSourceRows,
                  currentHasSourceRows,
                });
              }
              setLoading(false);
              return;
            }

            latestRawArbitrageRef.current = merged;
            hasReceivedArbitrageListRef.current = true;
            applyArbitragePayload(merged, {
              allowEmptyReplace: true,
              sourcePreviewMode: incomingHasSourceRows,
            });
            setLoading(false);
          }
        };
        ws.onerror = () => {
          try {
            ws.close();
          } catch {
            /* ignore */
          }
        };
        ws.onclose = () => {
          if (analysisWsRef.current === ws) analysisWsRef.current = null;
          if (cancelled) return;
          pushTransportActiveRef.current = false;
          analysisWsAttemptRef.current = Math.min(analysisWsAttemptRef.current + 1, 20);
          scheduleRestPoll();
          scheduleWsReconnect();
        };
      } catch {
        pushTransportActiveRef.current = false;
        analysisWsAttemptRef.current = Math.min(analysisWsAttemptRef.current + 1, 20);
        restTick();
        scheduleRestPoll();
        scheduleWsReconnect();
      }
    };

    (async () => {
      await restTick();
      if (cancelled) return;
      scheduleRestPoll();
      await connectAnalysisWs();
    })();

    return () => {
      cancelled = true;
      clearPoll();
      clearWsReconnect();
      pushTransportActiveRef.current = false;
      if (abortPollRef.current) {
        abortPollRef.current.abort();
        abortPollRef.current = null;
      }
      if (analysisWsRef.current) {
        try {
          analysisWsRef.current.close();
        } catch {
          /* ignore */
        }
        analysisWsRef.current = null;
      }
    };
  }, [active, scope, applyArbitragePayload, applyVisibleBatchMetaFromStatus]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profitDropdownRef.current && !profitDropdownRef.current.contains(e.target)) {
        setIsProfitOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsProfitOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    // When scope or sport filter changes, clear all grace state
    // immediately so old-filter rows don't bleed into new filter.
    const timers_     = leavingTimersRef.current;
    const fadeTimers_ = fadeTimersRef.current;
    const isFilterChange =
      prevScopeRef.current !== scope || prevSportRef.current !== sport;
    if (isFilterChange) {
      timers_.forEach((tid) => clearTimeout(tid));
      timers_.clear();
      fadeTimers_.forEach((tid) => clearTimeout(tid));
      fadeTimers_.clear();
      setLeavingKeys(new Set());
      setDisplayResults(results);
      // Reset to empty so the next render does NOT start grace timers
      // for items that belonged to the old scope. The correct keys for
      // the new scope will be populated on the next non-filter-change
      // render when the re-filtered results arrive.
      prevResultKeysRef.current = new Set();
      prevScopeRef.current = scope;
      prevSportRef.current = sport;
      return;
    }
    prevScopeRef.current = scope;
    prevSportRef.current = sport;

    const GRACE_MS = 15000;
    const FADE_MS  = 400;
    const timers     = leavingTimersRef.current;
    const fadeTimers = fadeTimersRef.current;

    const newKeys = new Set(
      results.map((m) => getItemStableIdentityKey(m)).filter(Boolean)
    );

    // Cancel timers for keys that reappeared
    for (const k of newKeys) {
      if (fadeTimers.has(k)) {
        clearTimeout(fadeTimers.get(k));
        fadeTimers.delete(k);
      }
      if (timers.has(k)) {
        clearTimeout(timers.get(k));
        timers.delete(k);
        setLeavingKeys((prev) => {
          const next = new Set(prev);
          next.delete(k);
          return next;
        });
      }
    }

    // Start grace timers for keys that just disappeared
    for (const k of prevResultKeysRef.current) {
      if (!newKeys.has(k) && !timers.has(k)) {
        const fadeTid = setTimeout(() => {
          fadeTimers.delete(k);
          setLeavingKeys((prev) => new Set([...prev, k]));
        }, GRACE_MS - FADE_MS);
        fadeTimers.set(k, fadeTid);

        const removeTid = setTimeout(() => {
          timers.delete(k);
          setLeavingKeys((prev) => {
            const next = new Set(prev);
            next.delete(k);
            return next;
          });
          setDisplayResults((prev) =>
            prev.filter((m) => getItemStableIdentityKey(m) !== k)
          );
        }, GRACE_MS);
        timers.set(k, removeTid);
      }
    }

    prevResultKeysRef.current = newKeys;

    // Merge active rows + rows still in grace period
    setDisplayResults((prev) => {
      const activeMap = new Map(
        results.map((m) => [getItemStableIdentityKey(m), m])
      );
      const leavingRows = prev.filter((m) => {
        const k = getItemStableIdentityKey(m);
        return !activeMap.has(k) && timers.has(k);
      });
      return [...results, ...leavingRows];
    });
  }, [results, scope, sport]);

  const isArbV2ProductionUiMode = ARB_V2_PRODUCTION_MODE_ENABLED && !ARB_V2_SOURCE_PREVIEW_ENABLED;
  const lastWsPayloadAt = Number(lastArbV2WsPayloadAtRef.current || 0);
  const hasRecentWsPayload = lastWsPayloadAt > 0 && Date.now() - lastWsPayloadAt < 20000;
  const hasWsPrimaryOrPayload = pushTransportActiveRef.current === true || hasRecentWsPayload;
  const showScanningState =
    loading && (!isArbV2ProductionUiMode || !hasWsPrimaryOrPayload);

  return (
    <div className="arb-page">
      <div className="arb-topbar">
        <div className="arb-segment">
          {scopeTabs.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setScope(item.key)}
              className={`arb-seg-btn ${scope === item.key ? "is-active" : ""}`}
            >
              {item.label}
            </button>
          ))}

          <div
            className="arb-seg-indicator"
            style={{
              width: `${indicatorWidth}%`,
              left: `${indicatorWidth * activeIndex}%`,
            }}
          />
        </div>

        <div ref={profitDropdownRef} className="arb-select-wrap">
          <button
            type="button"
            onClick={() => setIsProfitOpen((prev) => !prev)}
            className={`arb-select-button ${isProfitOpen ? "is-open" : ""}`}
            aria-haspopup="listbox"
            aria-expanded={isProfitOpen}
          >
            <span className="arb-select-button-text">≥{profitFilter}%</span>
            <span
              className="arb-select-arrow"
              style={{
                transform: isProfitOpen
                  ? "translateY(-50%) rotate(180deg)"
                  : "translateY(-50%) rotate(0deg)",
              }}
            >
              ▾
            </span>
          </button>

          {isProfitOpen ? (
            <div className="arb-dropdown-menu" role="listbox">
              {PROFIT_OPTIONS.map((value) => {
                const activeOption = profitFilter === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setProfitFilter(value);
                      setIsProfitOpen(false);
                    }}
                    className={`arb-dropdown-item ${activeOption ? "is-active" : ""}`}
                  >
                    ≥{value}%
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      <div className="arb-sport-wrap">
        <div className="arb-sport-scroll" role="tablist" aria-label={t.common.filter}>
          {sportTabs.map((item) => {
            const isActive = sport === item.key;
            const canonicalSportKey = mapSportTabToCanonicalSportKey(item.key);
            const badgeCount = Number(sportBadgeCounts[canonicalSportKey] || 0);

            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setSport(item.key)}
                className={`arb-sport-btn ${isActive ? "is-active" : ""}`}
              >
                <span className="arb-sport-btn-text">{item.label}</span>
                {!isActive && badgeCount > 0 ? (
                  <span
                    style={{
                      marginLeft: 8,
                      padding: "2px 8px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1.2,
                      background: "#e11d48",
                      color: "#fff",
                    }}
                  >
                    +{badgeCount}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <div className="arb-results-wrap">
        {showScanningState ? (
          <div className="arb-empty">{t.arbitrage.scanning}</div>
        ) : results.length === 0 ? (
          <div className="arb-empty">
            {isArbV2ProductionUiMode ? "Không tìm thấy cơ hội chênh kèo" : t.arbitrage.noArb}
            {isArbV2ProductionUiMode && hasWsPrimaryOrPayload ? (
              <div style={{ marginTop: 6, fontSize: 12, opacity: 0.72 }}>
                Đang chờ cơ hội Arb sạch từ WebSocket
              </div>
            ) : null}
          </div>
        ) : (
          <div className="arb-list">
            {displayResults.map((m) => {
              const stableK = getItemStableIdentityKey(m);
              const isLeaving = leavingKeys.has(stableK);
              return (
                <ArbCard
                  key={stableK ?? m.id ?? m.key}
                  item={m}
                  lang={lang}
                  t={t}
                  timeZone={timeZone}
                  isLeaving={isLeaving}
                  oddsFormat={oddsFormat}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------
RESULT CARD
-------------------- */
function isBadCardTimeDisplay(str) {
  if (!str || str === "—") return true;
  if (/1970|01\s*Jan|Jan\s*01|1970-01-01/i.test(str)) return true;
  return false;
}

function renderMatchWithColoredAt(match) {
  const raw = String(match ?? "");
  if (!raw) return raw;
  const parts = raw.split(" @ ");
  if (parts.length !== 2) return raw;
  return (
    <>
      <span>{parts[0]}</span>
      <span className="arb-at-sep"> @ </span>
      <span>{parts[1]}</span>
    </>
  );
}

function parseArbMatchParts(match) {
  const s = String(match ?? "").trim();
  if (s.includes(" @ ")) {
    const idx = s.indexOf(" @ ");
    return { left: s.slice(0, idx).trim(), right: s.slice(idx + 3).trim(), separatorType: "@" };
  }
  const vsMatch = / vs /i.exec(s);
  if (vsMatch) {
    return { left: s.slice(0, vsMatch.index).trim(), right: s.slice(vsMatch.index + vsMatch[0].length).trim(), separatorType: "vs" };
  }
  const vMatch = / v /i.exec(s);
  if (vMatch) {
    return { left: s.slice(0, vMatch.index).trim(), right: s.slice(vMatch.index + vMatch[0].length).trim(), separatorType: "v" };
  }
  if (s.includes(" - ")) {
    const idx = s.indexOf(" - ");
    return { left: s.slice(0, idx).trim(), right: s.slice(idx + 3).trim(), separatorType: "-" };
  }
  return { left: s, right: "", separatorType: "" };
}

function getArbHomeTeamName(item) {
  const explicit =
    (item?.homeTeamName && String(item.homeTeamName).trim()) ||
    (item?.homeTeam && String(item.homeTeam).trim()) ||
    (item?.teams?.home?.name && String(item.teams.home.name).trim()) ||
    (item?.event?.homeTeam && String(item.event.homeTeam).trim()) ||
    (item?.fixture?.homeTeam && String(item.fixture.homeTeam).trim()) ||
    (item?.fixture?.teams?.home?.name && String(item.fixture.teams.home.name).trim()) ||
    "";
  if (explicit) return explicit;
  const match = String(item?.match ?? "").trim();
  if (!match || match === "—") return "";
  const { left, right, separatorType } = parseArbMatchParts(match);
  if (separatorType === "@") return right;
  if (left) return left;
  return match;
}

function renderArbMatchWithCopy(item, copiedHome, onCopy, copiedLabel) {
  const match = String(item?.match ?? "—").trim();
  const homeName = getArbHomeTeamName(item);
  const { left, right, separatorType } = parseArbMatchParts(match);
  const hasSeparator = Boolean(separatorType && (left || right));
  const displayLeft = hasSeparator ? left : match;
  const displayRight = hasSeparator ? right : "";
  const showCopyBtn = homeName && homeName !== "—";

  return (
    <span style={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap" }}>
      {hasSeparator ? (
        <>
          <span>{displayLeft}</span>
          <span style={{ margin: "0 4px", color: "#7d89a8" }}>-</span>
          <span>{displayRight}</span>
        </>
      ) : (
        <span>{displayLeft}</span>
      )}
      {showCopyBtn ? (
        <button
          type="button"
          title={copiedHome ? (copiedLabel ?? "Copied") : `Copy ${homeName}`}
          aria-label={copiedHome ? (copiedLabel ?? "Copied") : `Copy ${homeName}`}
          onClick={(e) => onCopy(e, homeName)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 18,
            height: 18,
            marginLeft: 5,
            padding: 0,
            border: copiedHome ? "1px solid rgba(34,197,94,0.35)" : "1px solid rgba(180,197,235,0.18)",
            borderRadius: 4,
            background: copiedHome ? "rgba(34,197,94,0.12)" : "rgba(76,111,255,0.08)",
            color: copiedHome ? "#4ade80" : "#a8b3cf",
            fontSize: 10,
            cursor: "pointer",
            verticalAlign: "middle",
            lineHeight: 1,
            flexShrink: 0,
            transition: "color 0.15s, background 0.15s, border-color 0.15s",
          }}
        >
          {copiedHome ? "✓" : "📋"}
        </button>
      ) : null}
    </span>
  );
}

function ResultCard({ item, lang = "en-GB", t, timeZone, isLeaving, oddsFormat = "hk" }) {
  const statusInfo = getStatusLabel(item, t, lang, timeZone);
  const isEsports = getItemSport(item) === "esports";
  const [copiedHome, setCopiedHome] = useState(false);
  const copyResetTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
    };
  }, []);

  const handleCopyHomeTeam = async (e, homeName) => {
    e.preventDefault();
    e.stopPropagation();
    if (!homeName) return;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(homeName);
      } else {
        const ta = document.createElement("textarea");
        ta.value = homeName;
        ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedHome(true);
      if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
      copyResetTimerRef.current = setTimeout(() => setCopiedHome(false), 1000);
    } catch {
      /* ignore */
    }
  };

  const displayLeague = formatArbSportLeagueLabel(item, lang);

  let displayTimeValue = statusInfo.timeValue;
  if (statusInfo.isLive) {
    if (isBadCardTimeDisplay(displayTimeValue)) {
      displayTimeValue = "LIVE";
    }
  } else {
    if (isBadCardTimeDisplay(displayTimeValue)) {
      displayTimeValue = "—";
    }
    if (!displayTimeValue || displayTimeValue === "—") {
      const preferredKickoff = resolvePreferredKickoffCandidate(item);
      if (typeof preferredKickoff === "string" && isUsableKickoffString(preferredKickoff)) {
        displayTimeValue = formatAnalysisTime(preferredKickoff, lang, timeZone);
      } else if (typeof preferredKickoff === "number" && isUsableKickoffTs(preferredKickoff)) {
        const ms = kickoffTsToMs(preferredKickoff);
        displayTimeValue =
          Number.isFinite(ms) && !isSuspiciousEpochLikeMs(ms)
            ? formatAnalysisTime(ms, lang, timeZone)
            : "—";
      } else if (isUsableKickoffTs(item?.kickoffTs)) {
        const ms = kickoffTsToMs(item.kickoffTs);
        displayTimeValue =
          Number.isFinite(ms) && !isSuspiciousEpochLikeMs(ms)
            ? formatAnalysisTime(ms, lang, timeZone)
            : "—";
      } else {
        displayTimeValue = "—";
      }
    }
    if (isBadCardTimeDisplay(displayTimeValue)) {
      displayTimeValue = "—";
    }
  }

  const updatedTs = (() => {
    // cardUpdatedAt is pre-annotated from lastUpdateTs in annotateArbitrageDirection.
    if (
      typeof item.cardUpdatedAt === "number" &&
      Number.isFinite(item.cardUpdatedAt) &&
      item.cardUpdatedAt > 0 &&
      !isSuspiciousEpochLikeMs(item.cardUpdatedAt)
    ) {
      return item.cardUpdatedAt;
    }
    // Fallback: try item-level update fields directly (guarded against epoch-0/1970).
    for (const k of ["lastUpdateTs", "updatedAt", "lastUpdate"]) {
      const v = item[k];
      if (typeof v !== "number" || !Number.isFinite(v) || v <= 0) continue;
      if (isSuspiciousEpochLikeMs(v)) continue;
      return v;
    }
    return null;
  })();

  let updatedLabel = "";
  if (updatedTs != null) {
    try {
      updatedLabel = fmtTime(updatedTs, lang, timeZone);
    } catch {
      updatedLabel = "";
    }
  }

  const profitDirection = item?.profitDirection || "flat";
  const profitDirClass =
    profitDirection === "up"
      ? "is-profit-up"
      : profitDirection === "down"
        ? "is-profit-down"
        : "is-profit-flat";

  const pickDirMap = useMemo(() => {
    if (item?.pickDirections && typeof item.pickDirections === "object") {
      return item.pickDirections;
    }
    return {};
  }, [item?.pickDirections]);
  const picks = toArray(item?.picks);
  const teams = eventTeamsFromArbItemUi(item);
  const [flashMap, setFlashMap] = useState({});
  const flashTimerRef = useRef(null);
  const translatorBag = useMemo(() => getArbDisplayTranslatorBag(lang), [lang]);

  useEffect(() => {
    if (!updatedTs || !Number.isFinite(updatedTs)) {
      setFlashMap({});
      return undefined;
    }

    if (flashTimerRef.current) {
      clearTimeout(flashTimerRef.current);
      flashTimerRef.current = null;
    }

    const next = {};
    picks.forEach((p) => {
      const key = getPickStableKey(p);
      if (!key) return;
      const dir = pickDirMap[key];
      if (dir === "up" || dir === "down") {
        next[key] = dir;
      }
    });

    if (Object.keys(next).length > 0) {
      setFlashMap(next);
      flashTimerRef.current = setTimeout(() => {
        setFlashMap({});
        flashTimerRef.current = null;
      }, 2000);
    } else {
      setFlashMap({});
    }

    return () => {
      if (flashTimerRef.current) {
        clearTimeout(flashTimerRef.current);
        flashTimerRef.current = null;
      }
    };
  }, [updatedTs, picks, pickDirMap]);

  return (
    <div className={`arb-result-card${isLeaving ? " is-leaving" : ""}`}>
      <div className="arb-result-top">
        <div className="arb-result-main">
          <div className="arb-result-league-row">
            <span className="arb-result-league">
              {displayLeague}
            </span>
            <div
              className={`arb-status-time-pill ${
                statusInfo.isLive ? "is-live" : "is-prematch"
              }`}
            >
              <span className="arb-status-time-value">{displayTimeValue}</span>
            </div>
          </div>

          <div className="arb-result-match-row">
            <div className="arb-result-match">{renderArbMatchWithCopy(item, copiedHome, handleCopyHomeTeam, t.common.copied)}</div>
            <div className={`arb-result-profit ${profitDirClass}`}>
              <span className="arb-result-profit-value">
                +{toNumber(item.profitPct, 0).toFixed(2)}%
              </span>
            </div>
          </div>

          {isEsports ? (
            <div className="arb-result-market-row">
              <div className="arb-result-market">{getEsportsMarketLabel(item, lang)}</div>
              {updatedLabel ? (
                <div className="arb-result-updated-pill">
                  <span className="arb-result-updated-label">{t.common.updated}</span>{" "}
                  <span className="arb-result-updated-time">{updatedLabel}</span>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="arb-result-market-row">
              <div className="arb-result-market">
                {getClassicMarketRowLabel(item, lang) || "—"}
              </div>
              {updatedLabel ? (
                <div className="arb-result-updated-pill">
                  <span className="arb-result-updated-label">{t.common.updated}</span>{" "}
                  <span className="arb-result-updated-time">{updatedLabel}</span>
                </div>
              ) : null}
            </div>
          )}

          <div className="arb-result-picks-preview">
            {picks.length ? (
              picks.map((p, idx) => {
                const canonicalLeg =
                  typeof arbDisplayHelpers.buildArbCanonicalLegDisplayLabel === "function"
                    ? arbDisplayHelpers.buildArbCanonicalLegDisplayLabel(item, p, translatorBag)
                    : null;
                const selectionLabel =
                  (canonicalLeg && canonicalLeg.label) || formatClassicPickPreviewLabel(p, lang, item, null);

                // ── Home / Away badge ──────────────────────────────────────
                const _selRaw = String(p?.selection ?? p?.sel ?? "").trim();
                const _isOuSel = /^(over|under)\b/i.test(_selRaw);
                let homeBadge = null;
                if (!_isOuSel && teams.home && teams.away) {
                  const _isHome = selectionTextMatchesSingleTeamUi(_selRaw, teams.home);
                  const _isAway = !_isHome && selectionTextMatchesSingleTeamUi(_selRaw, teams.away);
                  const _isVi = String(lang || "").toLowerCase().startsWith("vi");
                  if (_isHome) homeBadge = _isVi ? "Chủ nhà" : "Home";
                  else if (_isAway) homeBadge = _isVi ? "Khách" : "Away";
                }
                // ──────────────────────────────────────────────────────────

                const bookLabel = formatBookDisplayName(
                  (canonicalLeg && String(canonicalLeg.sportsbook || "").trim()) ||
                  String(p.sportsbook || p.book || "").trim() ||
                  "—"
                );
                let oddsText = "";
                if (oddsFormat === "hk" && typeof arbDisplayHelpers.buildArbPickOddsHkDisplay === "function") {
                  oddsText = arbDisplayHelpers.buildArbPickOddsHkDisplay(p) || "";
                }
                if (!oddsText) {
                  oddsText = formatAnalysisOddsDisplay(p?.odds, oddsFormat) || "";
                }
                const oddsLabel = oddsText ? oddsText : "—";
                const stablePickKey = getPickStableKey(p);
                const flashDir = flashMap[stablePickKey] || "flat";
                const flashClass =
                  flashDir === "up"
                    ? "arb-pill-odds-up"
                    : flashDir === "down"
                      ? "arb-pill-odds-down"
                      : "";
                return (
                  <div key={`${getItemStableIdentityKey(item)}-preview-${idx}`} className="arb-pick-preview-row">
                    <div className="arb-pick-preview-sel" title={selectionLabel}>
                      {selectionLabel}
                      {homeBadge ? (
                        <span
                          className="arb-pill arb-pill-side"
                          style={{ marginLeft: 5, flexShrink: 0 }}
                        >
                          {homeBadge}
                        </span>
                      ) : null}
                    </div>
                    <div className="arb-pick-preview-right">
                      <span className={`arb-pill arb-pill-odds ${flashClass}`.trim()}>{oddsLabel}</span>
                      <span className="arb-pill arb-pill-book" title={bookLabel}>
                        {bookLabel}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="arb-pick-preview-row is-empty">
                <div className="arb-pick-preview-sel">
                  {getClassicSelectionRowLabel(item, lang) || "—"}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const ArbCard = React.memo(ResultCard);
